import { RootState } from "redux/store";
import { createSelector } from "reselect";
import { AgentMlsIds } from "model/agent-mls-ids";
import { buildAgentMlsIds } from "shared/agent-mls-ids";
import moment, { Moment } from "moment";
import { Campaign } from "model/campaign";
import { CampaignDisplayCategory } from "model/campaign-display-category";
import { groupDripCampaigns, sortCategoriesAlphabetically, GroupedDripCampaigns } from "shared/campaign-grouping";
import { EmailMarketingType } from "type/email-marketing-type";
import { CampaignStatus } from "type/campaign-status";
import { DynamicContentType } from "type/dynamic-content";
import { CampaignSubscription } from "model/campaign-subscription";
import { CampaignSubscriptionStatus } from "type/campaign-subscription-status";
import { CampaignEmail } from "model/campaign-email";
import { CampaignEmailStatus } from "type/campaign-email-status";
import { CampaignSubscriptionStat } from "model/campaign-subscription-stat";
import { CampaignStat } from "model/campaign-stat";
import { CampaignsSettings } from "model/campaigns-settings";
import { CampaignRoute } from "model/campaign-route";
import { CampaignMessage } from "model/campaign-message";
import { CampaignMessageStatus } from "type/campaign-message-status";
import { CampaignMessageType } from "type/campaign-message";
import { TimeUnitType } from "type/time-unit";
import { CampaignCampaignMessageStat } from "model/campaign-campaign-message-stat";
import { CampaignCampaignMessage } from "model/campaign-campaign-message";
import { City } from "model/city";
import { Client } from "model/client";
import { EmailClick } from "model/email-click";
import { IntegrationCalendar } from "model/integration-calendar";
import { Integration } from "model/integration";
import { IntegrationType } from "type/integration-type";
import { EmailSent } from "model/email-sent";
import { Language } from "model/language";
import { LeadActivity } from "model/lead-activity";
import { LeadActivityType } from "type/lead-activity-type";
import { TextMessageActivity } from "model/text-message-activity";
import { LeadActivityStat } from "model/lead-activity-stat";
import { LeadAnniversaryType } from "type/lead-anniversary";
import { LeadAnniversary } from "model/lead-anniversary";
import { LeadImport } from "model/lead-import";
import { LeadTagType } from "type/lead-tag";
import { ImportError } from "type/import-error";
import { LeadPipelineType } from "type/lead-pipeline";
import { LeadLastActivity } from "model/lead-last-activity";
import { LeadNote } from "model/lead-note";
import {
	SearchAgentProfile,
	SearchAgentConversationDay,
	SearchAgentConversationMessage,
	SearchAgentMessageRole,
} from "model/search-agent-profile";
import { LeadNoteType } from "type/lead-note-type";
import { LeadProfileSuggestion, LeadProfileSuggestionValue } from "model/lead-profile-suggestion";
import { LeadProfileSuggestionField } from "type/lead-profile-suggestion-field";
import { LeadProfileSuggestionValueResource } from "api/resource/lead-profile-suggestion";
import { LeadPhone } from "model/lead-phone";
import { GeographicRoute } from "model/geographic-route";
import { LeadPhoneType } from "type/lead-phone";
import { LeadPipeline } from "model/lead-pipeline";
import { Lead } from "model/lead";
import { LeadStatus } from "type/lead-status";
import { LeadRanking } from "type/lead-ranking";
import { Sort } from "component/shared/table";
import { LeadColumnType } from "type/lead-column";
import { LeadsPageFilters } from "model/leads-page-filters";
import { LeadTag } from "model/lead-tag";
import { Listing } from "model/listing";
import { formatPrice } from "shared/price-formatter";
import { MarketListing } from "model/market-listing";
import { MarketReportSubscription } from "model/market-report-subscription";
import { MarketReport } from "model/market-report";
import { MarketReportType } from "type/market-report-type";
import { Market } from "model/market";
import { Neighborhood } from "model/neighborhood";
import { OfficeBoard } from "model/office-board";
import { Office } from "model/office";
import { PostalCode } from "model/postal-code";
import { PageType } from "type/page-type";
import { Page } from "model/page";
import { Quota } from "model/quota";
import { Reseller } from "model/reseller";
import { SavedListing } from "model/saved-listing";
import { SavedSearch } from "model/saved-search";
import { numberedDateFormatter } from "shared/date-formatter";
import { countLeadsPageFilters } from "shared/lead-navigation";
import { getLeadsPageColumnTypes, searchLeads, sortLeads } from "shared/lead-list";
import { hasSearchAgent } from "shared/search-agent-permission";
import { SiteLink } from "model/site-link";
import { SiteLinkType } from "type/site-link-type";
import { Task } from "model/task";
import { TasksPageFiltersState } from "./slice/tasks-page";
import { UserAuth } from "model/user-auth";
import { TaskStatus } from "type/task-status";
import { TaskType } from "type/task-type";
import { Board } from "model/board";
import { KestrelVersion } from "type/kestrel-version";
import { ListingDetailPageLayoutType } from "type/listing-detail-page-layout";
import { LeadCaptureSidebarPositionType } from "type/lead-capture-sidebar-position";
import { LeadCaptureStrengthType } from "type/lead-capture-strength";
import { SearchPagePromptType } from "type/search-page-prompt";
import { SearchResultsPromptType } from "type/search-results-prompt";
import { SearchDetailPromptType } from "type/search-detail-prompt";
import { ActiveResultsSortType } from "type/active-results-sort";
import { EurekaResultsSortType } from "type/eureka-results-sort";
import { FeaturedSortCodeType } from "type/featured-sort-code";
import { ListingResultsDisplayType } from "type/listing-results-display";
import { LimitSoldResultsByMonthsType } from "type/limit-sold-results-by-months";
import { FeaturedPendingResultsType } from "type/featured-pending-results";
import { MapSearchMapType } from "type/map-search-map-type";
import { OpenHouseDisplayType } from "type/open-house-display";
import { OpenHouseSearchType } from "type/open-house-search";
import { MapPositionType } from "type/map-position";
import { WebsitePlatformType } from "type/website-platform";
import { User } from "model/user";
import { UserType } from "type/user-type";
import { AggregateStat } from "model/aggregate-stat";
import { Agent } from "model/agent";
import { AgentStaffType } from "type/agent-staff";
import { AgentBoard } from "model/agent-board";
import { ViewType } from "type/view-type";
import { RoundRobinRoute } from "model/round-robin-route";
import { PropertyType } from "type/property-type";
import { AgentSortOrderType } from "type/agent-sort-order";
import { OfficeSortOrderType } from "type/office-sort-order";
import { TextMessageBrandingStatus } from "type/text-message-branding-status";
import { AgentColumnType } from "type/agent-column-type";
import { MarketSettings } from "model/market-settings";
import { NaturalLanguageSearchSuggestionResource } from "api/resource/natural-language-search-suggestion";
import { AreaSelectorPlaceholderResource } from "api/resource/area-selector-placeholder";
import { EmailRoutingRuleResource } from "api/resource/email-routing-rule";
import { EmailRecipientResource } from "api/resource/email-recipient";
import { OffMarketListingResource } from "api/resource/off-market-listing";
import { ClientPropertyResource, ClientPropertyListItemResource } from "api/resource/client-property";
import { PhotoResource } from "api/resource/listing-photo";
import { LeadContactActivityResource, ContactEventTypeResource } from "api/resource/lead-contact-report";
import { SavedSearchActivityRowResource } from "api/resource/saved-search-activity";
import { SavedListingReportResource } from "api/resource/saved-listing-report";
import { MobileActivityReportResource } from "api/resource/mobile-activity";

// Constants to avoid creating new arrays in selectors
const EMPTY_ARRAY: number[] = [];
const EMPTY_LANG_IDS: number[] = [];
const EMPTY_IDS: number[] = [];
const EMPTY: number[] = [];
const EMPTY_NUM_IDS: number[] = [];

const selectState = (s: RootState) => s;
const selectIdParam = (_: RootState, id?: number) => (id ?? null);
const selectNumberIdsParam = (_: RootState, ids?: number[]) => ids ?? EMPTY_NUM_IDS;
const selectLeadIdParam = (_: RootState, leadId: number) => leadId;
const selectCampaignIdParam = (_: RootState, campaignId?: number) => (campaignId ?? null);

// AdminMode

export const getAdminMode = (state: RootState) => {
	return state.adminMode.enabled;
};

export const getViewType = createSelector(
	(state: RootState) => state.adminMode.view,
	(state: RootState) => state.adminMode.enabled,
	(view, enabled) => {
		let result = ViewType.getById(view);
		if (!result) {
			result = enabled ? ViewType.ADMIN : ViewType.CLIENT;
		}
		return result;
	}
);

// AgentBoards 

export const getAgentBoardById = createSelector(
	(state: RootState) => state.agentBoards.byId,
	(state: RootState, id?: number) => id,
	(agentBoardsById, id) => {
		if (id) {
			const agentBoard = agentBoardsById[id];
			if (agentBoard) {
				return {
					...agentBoard,
				} as AgentBoard;
			}
		}
		return undefined;
	}
);

export const getAgentBoardsByIds = createSelector(
	(state: RootState) => state.agentBoards.byId,
	(state: RootState, ids?: number[]) => ids || [],
	(agentBoardsById, ids) => {
		return ids
			.map((id) => {
				const agentBoard = agentBoardsById[id];
				return agentBoard ? { ...agentBoard } as AgentBoard : undefined;
			})
			.filter(Boolean) as AgentBoard[];
	}
);

export const getAgentBoards = createSelector(
	(state: RootState) => state.agentBoards.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId).map((id) => parseInt(id));
		return getAgentBoardsByIds(state, ids);
	}
);

export const getAgentBoardsForAgent = createSelector(
	(state: RootState) => state.agentBoards.byAgentId,
	(state: RootState, agentId: number) => agentId,
	(state: RootState, agentId: number) => state,
	(byAgentId, agentId, state) => {
		const ids = byAgentId[agentId];
		return getAgentBoardsByIds(state, ids);
	}
);

// Agents

const selectAgentsById = (s: RootState) => s.agents.byId;
const selectOfficesById = (s: RootState) => s.offices.byId;

export const getAgentById = createSelector(
  [selectAgentsById, selectOfficesById, selectIdParam],
  (agentsById, officesById, id): Agent | undefined => {
    if (id == null) return undefined;

    const agent = agentsById[id];
    if (!agent) return undefined;

    let name = '';
    if (agent.firstName) {
      name += agent.firstName;
      if (agent.lastName) {
        name += ' ';
        name += agent.lastName;
      }
    }
    let label = agent.email ?? '';
    if (name) label = name;

    const officeRaw =
      agent.officeId != null ? officesById[agent.officeId] : undefined;

    const office = officeRaw
      ? {
          ...officeRaw,
          name: officeRaw.name ?? '',
          address: officeRaw.address ?? '',
          city: officeRaw.city ?? '',
          priority: officeRaw.priority ?? 0,
        }
      : undefined;

    return {
      ...agent,
      name,
      label,
      lastLoginDate: agent.lastLoginDate
        ? moment(new Date(agent.lastLoginDate))
        : undefined,
      office,
      staffType: AgentStaffType.getById(agent.staffTypeId),
      languageIds: agent.languageIds ?? EMPTY_LANG_IDS,
      admin: !!agent.admin,
    };
  }
);

export const getAgentsByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getAgentById(state, id)).filter(Boolean) as Agent[];
};

const selectAgentIds = createSelector(
  [selectAgentsById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getAgents = (state: RootState) => {
	const ids = Object.keys(state.agents.byId).map((id) => parseInt(id));
	return getAgentsByIds(state, ids);
};

export const getAgentsByCustomSort = (state: RootState) => {
	const agents = getAgents(state);
	return agents.sort(function(a, b) {
		const sortOrderA = a.sortOrder ?? Number.MAX_VALUE;
		const sortOrderB = b.sortOrder ?? Number.MAX_VALUE;
		return sortOrderA - sortOrderB;
	});
};

export const getAgentsWithLeads = createSelector(
  [getAgents],
  (agents) => agents.filter((agent) => agent.ownsLeads)
);

export const getAgentTableColumns = createSelector(
	(state: RootState) => state.agentPage.columnTypeIds,
	(columnTypeIds) => {
		return columnTypeIds.map((id) => {
			return AgentColumnType.getById(id);
		}).filter(Boolean) as AgentColumnType[];
	}
);

// Agent MLS IDs
// The agent MLS ID read joined with the client boards and the agents, rebuilt only when one changes. The agent MLS ID
// read is for office-type accounts only; any other account builds from the client boards alone. Null until the reads it
// needs have loaded once; a reload after a write keeps showing the last snapshot.

const selectAgentMlsIdsById = (s: RootState) => s.agentMlsIds.byId;
const selectAgentMlsIdsLoading = (s: RootState) => s.agentMlsIds.loading;
const selectAgentMlsIdsError = (s: RootState) => s.agentMlsIds.error;
const selectClientBoardsById = (s: RootState) => s.clientBoards.byId;
const selectClientBoardsOfficeAccount = (s: RootState) => s.clientBoards.officeAccount;
const selectClientBoardsLoading = (s: RootState) => s.clientBoards.loading;
const selectClientBoardsError = (s: RootState) => s.clientBoards.error;
const selectAgentsLoading = (s: RootState) => s.agents.loading;
const selectAgentsError = (s: RootState) => s.agents.error;
// The service allows agent MLS IDs only on an office-type billing package, which reaches Control Panel as
// officeAccountType. That is not the broker permission: a seat-based plan with team features is a broker here but not on
// the server, and gets a 403.
const selectHasAgentMlsIds = (s: RootState) => s.authentication.user?.permissions?.officeAccountType === true;

export const getAgentMlsIds = createSelector(
	[
		selectAgentMlsIdsById, selectAgentMlsIdsLoading, selectAgentMlsIdsError,
		selectClientBoardsById, selectClientBoardsOfficeAccount, selectClientBoardsLoading, selectClientBoardsError,
		selectAgentsById, selectAgentsLoading, selectAgentsError,
		selectHasAgentMlsIds,
	],
	(agentMlsIdsById, agentMlsIdsLoading, agentMlsIdsError, clientBoardsById, officeAccount, clientBoardsLoading, clientBoardsError, agentsById, agentsLoading, agentsError, agentAccount): AgentMlsIds | null => {
		const entries = Object.values(agentMlsIdsById);
		const clientBoards = Object.values(clientBoardsById);
		const agents = Object.values(agentsById);
		// A read that failed before it ever loaded isn't an empty one: null lets the page show the error.
		const loaded = (loading: boolean | undefined, error: string | null | undefined, items: unknown[]) => items.length > 0 || (loading === false && !error);
		if (!loaded(clientBoardsLoading, clientBoardsError, clientBoards)
			|| (agentAccount && (!loaded(agentMlsIdsLoading, agentMlsIdsError, entries) || !loaded(agentsLoading, agentsError, agents)))) {
			return null;
		}
		// Billable is owning leads.
		const mlsIdAgents = agents.map((agent) => ({ id: agent.id, name: [agent.firstName, agent.lastName].filter(Boolean).join(" "), billable: agent.ownsLeads }));
		return buildAgentMlsIds(agentAccount ? entries : null, { officeAccount, clientBoards }, mlsIdAgents, agentAccount);
	}
);


// Postal Codes

export const getPostalCodeById = createSelector(
	(state: RootState) => state.postalCodes.byId,
	(state: RootState, id?: number) => id,
	(postalCodesById, id) => {
		if (id) {
			const postalCode = postalCodesById[id];
			if (postalCode) {
				return {
					...postalCode,
				} as PostalCode;
			}
		}
		return undefined;
	}
);

export const getPostalCodeByIds = createSelector(
	(state: RootState, ids: number[]) => ids || [],
	(state: RootState, ids: number[]) => state,
	(ids, state) => {
		return ids.map((id) => getPostalCodeById(state, id)).filter(Boolean) as PostalCode[];
	}
);

export const getPostalCodes = createSelector(
	(state: RootState) => state.postalCodes.byId,
	(state: RootState) => state,
	(postalCodesById, state) => {
		const ids = Object.keys(postalCodesById).map((id) => parseInt(id));
		return getPostalCodeByIds(state, ids);
	}
);


// Geographic Routes

export const getGeographicRouteById = (
	state: RootState,
	id?: number
): GeographicRoute | undefined => {
	if (id) {
		const geographicRoute = state.geographicRoutes.byId[id];
		const agent = getAgentById(state, geographicRoute.agentId);
		if (geographicRoute && agent) {
			return {
				...geographicRoute,
				agent,
				createdOn: moment(new Date(geographicRoute.createdOn)),
				updatedOn: moment(new Date(geographicRoute.updatedOn)),
			};
		}
	}
};

export const getGeographicRoutesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getGeographicRouteById(state, id)).filter(Boolean) as GeographicRoute[];
};

export const getGeographicRoutes = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.geographicRoutes.byId).map((id) => parseInt(id));
		return getGeographicRoutesByIds(state, ids);
	}
);

// Round Robin Routes

export const getRoundRobinRouteById = (
	state: RootState,
	id?: number
): RoundRobinRoute | undefined => {
	if (id) {
		const roundRobinRoute = state.roundRobinRoutes.byId[id];
		const agent = getAgentById(state, roundRobinRoute.agentId);
		if (roundRobinRoute && agent) {
			return {
				...roundRobinRoute,
				agent,
				createdOn: moment(new Date(roundRobinRoute.createdOn)),
			};
		}
	}
};

export const getRoundRobinRoutesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getRoundRobinRouteById(state, id)).filter(Boolean) as RoundRobinRoute[];
};

export const getRoundRobinRoutes = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.roundRobinRoutes.byId).map((id) => parseInt(id));
		return getRoundRobinRoutesByIds(state, ids);
	}
);

// AggregateStats

const selectAggById = (s: RootState) => s.aggregateStats.byId;
const selectAggIdParam = (_: RootState, id?: string | number) =>
  id != null ? String(id) : undefined;

export const getAggregateStatById = createSelector(
  [selectAggById, selectAggIdParam],
  (byId, id): AggregateStat | undefined => {
    if (!id) return undefined;
    const a = byId[id];
    if (!a) return undefined;

    const moreInfo = a.moreInfoRequests ?? 0;
    const sched = a.scheduleShowingRequests ?? 0;
    const valReq = a.valuationRequests ?? 0;
    const regs = a.userRegistrations ?? 0;
    const subs = a.newSubscribers ?? 0;
    const contacts = a.contactRequests ?? 0;
    const saves = a.listingSaves ?? 0;
    const newSave = a.newListingSearchSaves ?? 0;
    const views = a.listingViews ?? 0;
    const searches = a.listingSearches ?? 0;
    const poly = a.listingPolygonSearches ?? 0;

    const leadActions = moreInfo + sched + valReq + regs + subs + contacts + saves + newSave;
    const siteSearches = searches + poly;

    return {
      id: a.id,
      dateStamp: moment(new Date(a.dateStamp)),
      moreInfoRequests: moreInfo,
      scheduleShowingRequests: sched,
      valuationRequests: valReq,
      userRegistrations: regs,
      newSubscribers: subs,
      contactRequests: contacts,
      listingSaves: saves,
      newListingSearchSaves: newSave,
      totalListingSearchSaves: a.totalListingSearchSaves ?? 0,
      listingViews: views,
      listingSearches: searches,
      listingPolygonSearches: poly,
      leadActions,
      siteSearches,
    };
  }
);

const selectAggStatById = (s: RootState) => s.aggregateStats.byId;
const selectAggregateIdsParam = (_s: RootState, ids?: string[]) => ids ?? EMPTY_IDS;

export const getAggregateStatsByIds = createSelector(
  [selectState, selectAggregateIdsParam],
  (state, ids): AggregateStat[] =>
    ids.map((id) => getAggregateStatById(state, id)).filter(Boolean) as AggregateStat[]
);

const selectAggregateStatIds = createSelector(
  [selectAggStatById],
  (byId) => Object.keys(byId)
);

export const getAggregateStats = createSelector(
  [selectState, selectAggregateStatIds],
  (state, ids) => getAggregateStatsByIds(state, ids)
);
export const getChronologicalAggregateStats = (state: RootState) => {
	const aggregateStats = getAggregateStats(state);
	if (aggregateStats.length > 1) {
		aggregateStats.sort((a, b)=>{
			return b.dateStamp.valueOf() - a.dateStamp.valueOf();
		})
	}
	return aggregateStats;
};

export const getLatestAggregateStat = createSelector(
	(state: RootState) => state,
	(state) => {
		return getChronologicalAggregateStats(state)[0];
	}
);

export const getLatestExtrapolatedAggregateStat = createSelector(
	(state: RootState) => state,
	(state) => {
		const aggregateStat = getLatestAggregateStat(state);
		if (aggregateStat) {
			const now = moment();
			const currentDayOfMonth = now.date();
			const totalDaysInMonth = now.daysInMonth();
			const excluded = [
				"id",
				"dateStamp",
				"totalListingSearchSaves",
				"subscribers",
			];
			// Create a new object instead of mutating the original
			const extrapolatedStat: AggregateStat = { ...aggregateStat };
			Object.keys(extrapolatedStat).forEach((key) => {
				if (!excluded.includes(key)) {
					// @ts-ignore
					const value = extrapolatedStat[key];
					const newValue = Math.round((value / currentDayOfMonth) * totalDaysInMonth);
					// @ts-ignore
					extrapolatedStat[key] = newValue;
				}
			});
			return extrapolatedStat;
		}
		return aggregateStat;
	}
);

// Authentication

export const getAuthError = (state: RootState) => {
	return state.authentication.error;
};

const selectOneUser = (state:	RootState) => state.oneUsers.user;
const selectUser = (state: RootState) => state.authentication.user;
const selectAgents = (state: RootState) => state.agents;

const selectUserPermissions = createSelector([selectUser], (user) => {
	if (!user) return undefined;
	const p = user.permissions;

	const crm = p.crmNotes || p.crmTags || p.crmTasks || p.crmPhones || p.crmAnniversary || p.crmPipeline
	return {
		...p,
		report: p.listingReport || p.openHomeReport || p.marketReport,
		leads: p.leadSaveSearch || p.leadSaveListing || p.leadEmailUpdates || p.leadPropertyOrganizer || p.leadRequestMoreInfo || p.leadScheduleShowing || p.leadUserRegistration || p.leadValuationRequest || p.leadContactForm,
		leadRouting: p.geographicRouting || p.roundRobinRouting || p.listingAgentRouting,
		crm: crm,
		crmUpgrade: (!crm && !user.agentId && user.reseller.direct && !p.oneCrm && !p.broker),
		leadLastActive: true,
		crmTasks: p.crmTasks,
		crmBulkActions: crm,
		mobileApp: !p.oneCrm && !p.broker && (user.reseller.direct || user.reseller.id === 706),
		emailCalendarIntegration: p.emailCalendarIntegration && p.blastCampaigns,
		automatedGreetings: p.emailCalendarIntegration && p.crmAnniversary,
		kestrelAll: p.kestrelAll,
	};
});

const selectUserDates = createSelector([selectUser], (user): {
	contractStart: Moment | undefined;
	accountPaidDate: Moment | undefined;
	dripMMSOptOutDate: Date | null;
} => {
	return {
		contractStart: user?.contractStart ? moment(new Date(user.contractStart)) : undefined,
		accountPaidDate: user?.accountPaidDate ? moment(new Date(user.accountPaidDate)) : undefined,
		dripMMSOptOutDate: user?.dripMMSOptOutDate ? new Date(user.dripMMSOptOutDate) : null,
	};
});

const selectUserPreferences = createSelector([selectUser], (user) => {

	const kestralVersion = KestrelVersion.getById(user?.kestrelVersion);
	
	return {
		mapSearchMapType: MapSearchMapType.getById(user?.mapSearchMapTypeId) || MapSearchMapType.ROADMAP,
		kestrelVersion: kestralVersion ? kestralVersion : KestrelVersion.OFF,
		listingDetailSidebarPosition: LeadCaptureSidebarPositionType.getById(user?.listingDetailSidebarPosition) || LeadCaptureSidebarPositionType.SIDEBAR_RIGHT,
		settingListingDetailPageStyle: ListingDetailPageLayoutType.getById(user?.settingListingDetailPageStyle),
		leadCaptureType: user?.leadCaptureType ? LeadCaptureStrengthType.getById(user?.leadCaptureType) : undefined,
		searchPagePromptType: user?.searchPagePromptType ? SearchPagePromptType.getById(user?.searchPagePromptType) : undefined,
		searchResultsPromptType: user?.searchResultsPromptType ? SearchResultsPromptType.getById(user?.searchResultsPromptType) : undefined,
		searchDetailPromptType: user?.searchDetailPromptType ? SearchDetailPromptType.getById(user?.searchDetailPromptType) : undefined,
		activeResultsSort: ActiveResultsSortType.getByIdOrDefault(user?.activeResultsSortId),
		eurekaResultsSort: EurekaResultsSortType.getByIdOrDefault(user?.eurekaResultsSortId),
		featuredSortCode: FeaturedSortCodeType.getByIdOrDefault(user?.featuredSortCodeId),
		listingResultsDisplay: ListingResultsDisplayType.getByIdOrDefault(user?.listingResultsDisplayId),
		limitSoldResultsByMonths: LimitSoldResultsByMonthsType.getByIdOrDefault(user?.limitSoldResultsByMonthsId),
		featuredPendingResults: FeaturedPendingResultsType.getByIdOrDefault(user?.featuredPendingResultsId),
		openHouseDisplayType: OpenHouseDisplayType.getByIdOrDefault(user?.openHouseDisplayTypeId),
		openHouseSearchType: OpenHouseSearchType.getByIdOrDefault(user?.openHouseSearchTypeId),
		mapPosition: MapPositionType.getByIdOrDefault(user?.mapPosition),
		agentSortOrder: AgentSortOrderType.getByIdOrDefault(user?.agentSortOrder),
		officeSortOrder: OfficeSortOrderType.getByIdOrDefault(user?.officeSortOrder),
		automatedFeaturedSolds: !!user?.automatedFeaturedSolds,
		hideSoldSearch: !!user?.hideSoldSearch,
		showClientListingsFirst: !!user?.showClientListingsFirst,
		soldsOnFeatured: !!user?.soldsOnFeatured,
		showSupplementalPropertiesOnFeatured: !!user?.showSupplementalPropertiesOnFeatured,
		listingDetailModal: !!user?.listingDetailModal,
	};
});

export const getUserUnsafe = createSelector(
	[
		selectUser,
		selectUserPermissions,
		selectUserDates,
		selectUserPreferences,
		selectAgents,
		selectOneUser,
		(state: RootState) => state.offices?.byId ?? {},
	],
	(user, permissions, dates, preferences, agents, oneUser, offices): User | null | undefined => {
		if (user === undefined) {
			return;
		}
		if (user === null) {
			return null;
		}
		if (user && permissions) {
			let onePermissions = {};
			if (oneUser) {
				onePermissions = oneUser.permissions;
			}
			let admin = user.admin || false;
			if (user.clientId && !user.agentId) {
				admin = true;
			}
			const textAccountStatus = user.textMessageBrandingStatus ? TextMessageBrandingStatus.getById(user.textMessageBrandingStatus) : undefined;
			
			// Get agent directly from agents state to avoid circular dependency
			let agent: Agent | undefined;
			if (user.agentId && agents.byId[user.agentId]) {
				const agentData = agents.byId[user.agentId];
				let name = "";
				if (agentData.firstName) {
					name += agentData.firstName;
					if (agentData.lastName) {
						name += " ";
						name += agentData.lastName;
					} 
				}
				let label = "";
				if (agentData.email) {
					label = agentData.email;
				}
				if (name) {
					label = name;
				}
				
				let office: Office | undefined;
				if (agentData.officeId && offices[agentData.officeId]) {
					const officeData = offices[agentData.officeId];
					office = {
						...officeData,
						name: officeData.name || '',
						address: officeData.address || '',
						city: officeData.city || '',
						priority: officeData.priority || 0,
					};
				}
				
				agent = {
					...agentData,
					name,
					label,
					lastLoginDate: agentData.lastLoginDate ? moment(new Date(agentData.lastLoginDate)) : undefined,
					office,
					staffType: AgentStaffType.getById(agentData.staffTypeId),
					languageIds: agentData.languageIds || [],
					admin: agentData.admin ? agentData.admin : false,
				};
			}
			
			return {
				...user,
					...preferences,
					...dates,
				permissions: permissions,
				textMessagePhoneNumber: user.textMessagePhoneNumber || "",
				websitePlatform:  WebsitePlatformType.getOrCreateById(user?.websitePlatform),
				ownsLeads: user.ownsLeads,
				isAccountOwner: !!user.isAccountOwner,
				type: user.agentId ? UserType.AGENT : UserType.CLIENT,
				name: user.firstName + " " + user.lastName,
				reseller: {
					...user.reseller,
				},
				agent,
				admin: admin,
				textMessageAreaCode: user.textMessageAreaCode,
				textMessageCallForwarding: user.textMessageCallForwarding,
				tcpaCompliantTimeZone: user.tcpaCompliantTimeZone,
				textMessageOptIn: user.textMessageOptIn,
				dripMMSOptOutDate: user.dripMMSOptOutDate,
				textMessageBrandingStatus: textAccountStatus,
				onePermissions: onePermissions,
				lastMobileLogin: user.lastMobileLogin
					? moment(new Date(user.lastMobileLogin))
					: undefined,
					
			};
		}
		return undefined;
	}
);

export const getUser = (state: RootState) => {
	return getUserUnsafe(state) as User;
};


// Boards

export const getBoardById = (state: RootState, id?: number): Board | undefined => {
	if (id) {
		return state.boards.byId[id];
	}
};


const selectBoardsById = (state: RootState) => state.boards.byId;
const selectIdsParam = (_state: RootState, ids?: number[]) => ids ?? EMPTY_IDS;

export const getBoardsByIds = createSelector(
  [selectBoardsById, selectIdsParam],
  (boards, ids): Board[] => ids.map((id) => boards[id]).filter(Boolean) as Board[]
);

const selectAllBoardIds = (state: RootState) => 
  Object.keys(state.boards.byId).map((id) => parseInt(id));

export const getBoards = createSelector(
  [selectBoardsById, selectAllBoardIds],
  (boards, ids): Board[] => ids.map((id) => boards[id]).filter(Boolean) as Board[]
);

const selectUserBoardIds = (state: RootState) =>
  state.authentication.user?.boardIds ?? EMPTY_IDS;

export const getBoardsForUser = createSelector(
  [selectBoardsById, selectUserBoardIds],
  (boards, ids): Board[] => ids.map((id) => boards[id]).filter(Boolean) as Board[]
);

export const isCanadianOnly = createSelector(
	getBoardsForUser,
	(boards) => boards.every((board) => board.isCanadian)
);

export const canDisplayExternalLinksOnPropResultsAndDetails = createSelector(
	(state: RootState) => getBoardsForUser(state),
	(boards) => boards.every((board) => board.externalLinkDisplayType === 1)
);

export const canShowPhotoLayoutOptions = createSelector(
	(state: RootState) => getBoardsForUser(state),
	(boards) => boards.every((board) => !board.isContiguous)
);

export const canShowSupplementalOnSeparatePage = createSelector(
	(state: RootState) => getBoardsForUser(state),
	(boards) => boards.every((board) => !board.isContiguous)
);

export const canShowSupplementalListings = createSelector(
	getBoardsForUser,
	(boards) => boards.every((board) => board.allowsSupplementalListings === true)
);

// Team permissions
export const getMaxForTeams = createSelector([selectUser, selectUserPermissions], (user, permissions): boolean => {
	if (!user || !permissions) return false;
	return !!(permissions.broker && permissions.crm);
});

export const getIsAtLeadOwnershipLimit = createSelector(
	[selectUser, (state: RootState) => getAgents(state)],
	(user, agents): boolean => {
		if (!user) return false;
		if (user.permissions.limitTwoAgentsWithLeads) return agents.filter(a => a.ownsLeads).length >= 2;
		return false;
	}
);

export const getMaxLeadOwners = createSelector([selectUser], (user): number => {
	if (!user) return 0;
	if (user.permissions.limitTwoAgentsWithLeads) return 2;
	return Infinity;
});

// Campaigns
const selectCampaignsById = (s: RootState) => s.campaigns.byId;

export const getCampaignById = createSelector(
  [selectCampaignsById, selectState, selectIdParam],
  (byId, state, id): Campaign | undefined => {
    if (id == null) return undefined;

    const campaign = byId[id];
    if (!campaign) return undefined;

    const status = campaign.status ? CampaignStatus.getById(campaign.status) : undefined;
    let emailMarketingType =
      campaign.emailMarketingTypeId ? EmailMarketingType.getById(campaign.emailMarketingTypeId) : undefined;
    if (!emailMarketingType) emailMarketingType = EmailMarketingType.DRIP;

    const dynamicContentType = campaign.campaignType ?? '';
    const campaignType = DynamicContentType.getById(dynamicContentType);

    return {
      ...campaign,
      status: status ?? CampaignStatus.INACTIVE,
      get campaignCampaignMessages() {
        return getCampaignCampaignMessagesByCampaignId(state, campaign.id);
      },
      get subscriptions() {
        return getCampaignSubscriptionsForCampaign(state, campaign.id);
      },
      campaignType,
      emailMarketingType,
      dateCreated: moment(new Date(campaign.dateCreated)),
      dateModified: campaign.dateModified ? moment(new Date(campaign.dateModified)) : undefined,
      inactiveYn: campaign.inactiveYn,
    };
  }
);

// Optimized version without expensive getters and state dependency
export const getCampaignByIdOptimized = createSelector(
  [selectCampaignsById, selectIdParam],
  (byId, id): Campaign | undefined => {
    if (id == null) return undefined;

    const campaign = byId[id];
    if (!campaign) return undefined;

    const status = campaign.status ? CampaignStatus.getById(campaign.status) : undefined;
    let emailMarketingType =
      campaign.emailMarketingTypeId ? EmailMarketingType.getById(campaign.emailMarketingTypeId) : undefined;
    if (!emailMarketingType) emailMarketingType = EmailMarketingType.DRIP;

    const dynamicContentType = campaign.campaignType ?? '';
    const campaignType = DynamicContentType.getById(dynamicContentType);

    return {
      ...campaign,
      status: status ?? CampaignStatus.INACTIVE,
      // Remove expensive getters that depend on state - compute these separately when needed
      campaignCampaignMessages: [],
      subscriptions: [],
      campaignType,
      emailMarketingType,
      dateCreated: moment(new Date(campaign.dateCreated)),
      dateModified: campaign.dateModified ? moment(new Date(campaign.dateModified)) : undefined,
      inactiveYn: campaign.inactiveYn,
    };
  }
);

export const getCampaignsByIds = createSelector(
  [selectState, selectNumberIdsParam],
  (state, ids): Campaign[] =>
    ids.map((id) => getCampaignById(state, id)).filter(Boolean) as Campaign[]
);

const selectCampaignIds = createSelector(
  [selectCampaignsById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getCampaigns = createSelector(
  [selectState, selectCampaignIds],
  (state, ids) => getCampaignsByIds(state, ids)
);

// Optimized version that doesn't depend on the entire state
// This avoids re-renders when unrelated parts of state change
export const getCampaignsOptimized = createSelector(
  [selectCampaignsById],
  (byId): Campaign[] => {
    const ids = Object.keys(byId).map((k) => parseInt(k, 10));
    return ids.map((id) => {
      const campaign = byId[id];
      if (!campaign) return undefined;

      const status = campaign.status ? CampaignStatus.getById(campaign.status) : undefined;
      let emailMarketingType =
        campaign.emailMarketingTypeId ? EmailMarketingType.getById(campaign.emailMarketingTypeId) : undefined;
      if (!emailMarketingType) emailMarketingType = EmailMarketingType.DRIP;

      const dynamicContentType = campaign.campaignType ?? '';
      const campaignType = DynamicContentType.getById(dynamicContentType);

      return {
        ...campaign,
        status: status ?? CampaignStatus.INACTIVE,
        // Return empty arrays to avoid calling other selectors and causing re-renders
        campaignCampaignMessages: [],
        subscriptions: [],
        campaignType,
        emailMarketingType,
        dateCreated: moment(new Date(campaign.dateCreated)),
        dateModified: campaign.dateModified ? moment(new Date(campaign.dateModified)) : undefined,
        inactiveYn: campaign.inactiveYn,
      };
    }).filter(Boolean) as Campaign[];
  }
);

const getActiveCampaigns = createSelector(
	(state: RootState) => state,
	(state) => {
		return getCampaigns(state)
			.filter(campaign => campaign && campaign.status && (campaign.status === CampaignStatus.ACTIVE) && !campaign.inactiveYn)
		;
	}
);

export const getDripCampaigns = createSelector(
	(state: RootState, campaigns: Campaign[]) => state,
	(state: RootState, campaigns: Campaign[]) => campaigns,
	(state, campaigns) => {
		campaigns.forEach((campaign) => {
			campaign.campaignStat = getCampaignStatsByCampaignId(state, campaign.id);
		});
		campaigns.sort((a, b) => {
			if (a.name < b.name) {
				return -1;
			} else if (a.name > b.name) {
				return 1;
			}
			return 0;
		});
		return campaigns;
	}
);

export const getActiveDripCampaigns = createSelector(
	(state: RootState) => state,
	(state) => {
		const campaigns = getActiveCampaigns(state).filter(campaign => campaign.emailMarketingType.drip);
		return getDripCampaigns(state, campaigns);
	}
);

export const getAllDripCampaigns = createSelector(
	(state: RootState) => state,
	(state) => {
		const campaigns = getCampaigns(state).filter(campaign => campaign.emailMarketingType.drip && !campaign.inactiveYn);
		return getDripCampaigns(state, campaigns);
	}
);

// CampaignDisplayCategories (MAX-3878)
// Resource and model share the same {id, name} shape, so byId doubles as the model store —
// no translation step. Full 6-step selector hierarchy per CLAUDE.md.

/* stable input */
const selectCampaignDisplayCategoriesById = (s: RootState) => s.campaignDisplayCategories.byId;

export const getCampaignDisplayCategoryById = createSelector(
	[selectCampaignDisplayCategoriesById, selectIdParam],
	(byId, id): CampaignDisplayCategory | undefined => {
		if (id == null) return undefined;
		return byId[id];
	}
);

export const getCampaignDisplayCategoriesByIds = createSelector(
	[selectCampaignDisplayCategoriesById, selectNumberIdsParam],
	(byId, ids): CampaignDisplayCategory[] =>
		ids.map((id) => byId[id]).filter(Boolean) as CampaignDisplayCategory[]
);

const selectCampaignDisplayCategoryIds = createSelector(
	[selectCampaignDisplayCategoriesById],
	(byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getCampaignDisplayCategories = createSelector(
	[selectCampaignDisplayCategoriesById, selectCampaignDisplayCategoryIds],
	(byId, ids) => ids.map((id) => byId[id]).filter(Boolean) as CampaignDisplayCategory[]
);

// Specialized: alphabetical by name (AM3). Does not include the virtual Uncategorized bucket.
export const getSortedCampaignDisplayCategories = createSelector(
	[getCampaignDisplayCategories],
	(categories) => sortCategoriesAlphabetically(categories)
);

// Render selector for the grouped Campaigns page: real categories (alphabetical) each with their
// drip campaigns, plus the synthesized Uncategorized bucket pinned last. Counts + total are derived
// here (single source — the render never re-computes them). Grouping runs AFTER getAllDripCampaigns'
// in-place name sort/mutation.
export const getGroupedDripCampaigns = createSelector(
	[getCampaignDisplayCategories, getAllDripCampaigns],
	(categories, campaigns): GroupedDripCampaigns => groupDripCampaigns(categories, campaigns)
);

export const getAnniversaryCampaignByType = createSelector(
	(state: RootState, type: EmailMarketingType) => getCampaigns(state),
	(state: RootState, type: EmailMarketingType) => type,
	(campaigns, type) => {
		return campaigns.find(campaign => {
			if (campaign.emailMarketingType === type) {
				let campaignMessage = null;
				const campaignCampaignMessage = campaign.campaignCampaignMessages[0];
				if (campaignCampaignMessage) {
					campaignMessage = campaignCampaignMessage.campaignMessage;
					if (campaignMessage) {
						return true;
					}
				}
			}
			return false;
		});
	}
);

const getBlastCampaigns = createSelector(
	(state: RootState) => state,
	(state) => {
		return getActiveCampaigns(state).filter(campaign => campaign.emailMarketingType.blast);
	}
);

export const getSentBlastCampaigns = createSelector(
	(state: RootState) => state,
	(state) => {
		const draftCampaignIds = getDraftBlastCampaigns(state).map(campaign => campaign.id);
		return getBlastCampaigns(state).filter(campaign => !draftCampaignIds.includes(campaign.id));
	}
);

export const getDraftBlastCampaigns = createSelector(
    (state: RootState) => state,
    (state) => {
        return getBlastCampaigns(state).filter(campaign => {
			let scheduled;
			 if (campaign.campaignCampaignMessages.length && campaign.campaignCampaignMessages[0].campaignMessage.timeToSendNumber) {
				scheduled = true;
			 }
			const subsriptionsAdded = campaign.subscriptions.length;
			const emailsHaveBeenSent = campaign.subscriptions.some(subscription => subscription.emails.length !== 0);
			if (scheduled) {
				if (!emailsHaveBeenSent) {
					// if scheduled is true and emails have not been sent, then it is a draft
					return true;
				}
			} else if (!subsriptionsAdded) {
				// if there are no subscriptions added, then it is a draft
				return true;
			}
			return false;
        });
    }
); 

export const getUnsubscribedCampaigns = createSelector(
	(state: RootState, leadId: number) => getCampaignSubscriptionsForLead(state, leadId),
	(state: RootState, leadId: number) => getActiveDripCampaigns(state),
	(subscriptions, activeDripCampaigns) => {
		let subscribedCampaignIds: number[] | undefined;
		if (subscriptions) {
			subscribedCampaignIds = subscriptions.map((subscription) => subscription.campaign.id);
		}
		return activeDripCampaigns.filter(campaign => {
			if (subscribedCampaignIds !== undefined && subscribedCampaignIds.includes(campaign.id)) {
				return false;
			}
			return true;
		});
	}
);

// CampaignSubscriptions

const selectCampaignSubsById = (s: RootState) => s.campaignSubscriptions.byId;
const selectCampaignSubIdParam = (_: RootState, id?: number) => (id ?? null);
const selectCampaignSubIdsParam = (_: RootState, ids?: number[]) => ids ?? EMPTY_NUM_IDS;

export const getCampaignSubscriptionById = createSelector(
  [selectCampaignSubsById, selectState, selectCampaignSubIdParam],
  (subsById, state, id): CampaignSubscription | undefined => {
    if (id == null) return undefined;

    const subscription = subsById[id];
    if (!subscription) return undefined;

    const status = subscription.status ? CampaignSubscriptionStatus.getById(subscription.status) : undefined;
    const campaign = getCampaignById(state, subscription.campaignId);
    const lead = getLeadById(state, subscription.leadId);

    if (!status || !campaign || !lead) return undefined;

    return {
      ...subscription,
      createdOn: moment(new Date(subscription.createdOn)),
      status,
      get stage() {
        return this.emails.length;
      },
      get remaining() {
        const done = this.stage ?? 0;
        return Math.max(0, this.campaign.campaignCampaignMessages.length - done);
      },
      campaign,
      get emails() {
        return getCampaignEmailsByLeadIdAndCampaignId(state, subscription.leadId, subscription.campaignId);
      },
      lead,
    };
  }
);

export const getCampaignSubscriptionsByIds = createSelector(
  [selectState, selectCampaignSubIdsParam],
  (state, ids): CampaignSubscription[] =>
    ids.map((id) => getCampaignSubscriptionById(state, id)).filter(Boolean) as CampaignSubscription[]
);

const selectCampaignSubscriptionIds = createSelector(
  [selectCampaignSubsById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getCampaignSubscriptions = createSelector(
  [selectState, selectCampaignSubscriptionIds],
  (state, ids) => getCampaignSubscriptionsByIds(state, ids)
);

export const getDripCampaignSubscriptions = createSelector(
	(state: RootState) => state,
	(state) => {
		return getCampaignSubscriptions(state).filter(campaignSubscription => campaignSubscription.campaign.emailMarketingType.drip);
	}
);

export const getBlastCampaignSubscriptions = createSelector(
	(state: RootState) => state,
	(state) => {
		return getCampaignSubscriptions(state).filter(campaignSubscription => campaignSubscription.campaign.emailMarketingType.blast);
	}
);

export const getCampaignSubscriptionsForCampaign = createSelector(
	(state: RootState, campaignId: number) => getCampaignSubscriptions(state),
	(state: RootState, campaignId: number) => campaignId,
	(subscriptions, campaignId) => {
		return subscriptions.filter((campaignSubscription) => {
			const status = (campaignSubscription.status) ? campaignSubscription.status.deleted : undefined;
			return campaignSubscription.campaign.id === campaignId && !status
		});
	}
);

export const getTotalBlastCampaignSubscriptionsWithin24Hours = createSelector(
	(state: RootState) => state,
	(state) => {
		return getBlastCampaignSubscriptions(state)
			.filter(subscription => {
				const yesterday = moment().subtract(1, "days");
				if (subscription.campaign.dateModified) {
					return subscription.campaign.dateModified.isAfter(yesterday);
				}
				return subscription.campaign.dateCreated.isAfter(yesterday);
			})
			.length
		;
	}
);

export const getCampaignSubscriptionsForLead = createSelector(
	(state: RootState, leadId: number) => state.campaignSubscriptions.byLeadId[leadId],
	(state: RootState, leadId: number) => state,
	(ids, state) => {
		return getCampaignSubscriptionsByIds(state, ids);
	}
);

export const getDripCampaignSubscriptionsForLead = createSelector(
	(state: RootState, leadId: number) => getCampaignSubscriptionsForLead(state, leadId),
	(subscriptions) => {
		return subscriptions.filter(campaignSubscription => campaignSubscription.campaign.emailMarketingType.drip);
	}
);

const getCampaignSubscriptionsForLeads = createSelector(
	(state: RootState, leadIds: number[]) => state,
	(state: RootState, leadIds: number[]) => leadIds,
	(state, leadIds) => {
		let leadsSubscriptions: CampaignSubscription[] = [];
		leadIds.forEach((leadId) => {
			const subscriptionIds = state.campaignSubscriptions.byLeadId[leadId];
			if (subscriptionIds) {
				subscriptionIds.forEach((id) => {
					const subscription = getCampaignSubscriptionById(state, id);
					if (subscription) {
						leadsSubscriptions.push(subscription);
					}
				});
			}
		});
		return leadsSubscriptions;
	}
);

export const getDripCampaignSubscriptionsForLeads = createSelector(
	(state: RootState, leadIds: number[]) => getCampaignSubscriptionsForLeads(state, leadIds),
	(subscriptions) => {
		return subscriptions.filter(campaignSubscription => campaignSubscription.campaign.emailMarketingType.drip);
	}
);

export const getActiveDripCampaignSubscriptionsForLead = createSelector(
	(state: RootState, leadId: number) => getDripCampaignSubscriptionsForLead(state, leadId),
	(subscriptions) => {
		return subscriptions.filter((subscription) => {
			const status = (subscription.status) ? subscription.status.active : undefined;
			return status;
		});
	}
);

// CampaignEmails

const selectEmailsById = (s: RootState) => s.campaignEmails.byId;
const selectEmailIdParam = (_: RootState, id?: number) => (id ?? null);

export const getCampaignEmailById = createSelector(
  [selectEmailsById, selectState, selectEmailIdParam],
  (byId, state, id): CampaignEmail | undefined => {
    if (id == null) return undefined;

    const campaignEmail = byId[id];
    if (!campaignEmail) return undefined;

    const campaignMessage = getCampaignMessageById(state, campaignEmail.campaignMessageId);

    let status = CampaignEmailStatus.PENDING;
    if (campaignEmail.failedOn) status = CampaignEmailStatus.FAILED;
    if (campaignEmail.deliveredOn) status = CampaignEmailStatus.SUCCESS;

    if (!campaignMessage) return undefined;

    return {
      ...campaignEmail,
      campaignMessage,
      status,
      deliveredOn: campaignEmail.deliveredOn ? moment(new Date(campaignEmail.deliveredOn)) : undefined,
      failedOn:    campaignEmail.failedOn ? moment(new Date(campaignEmail.failedOn)) : undefined,
      openedOn:    campaignEmail.openedOn ? moment(new Date(campaignEmail.openedOn)) : undefined,
      clickedOn:   campaignEmail.clickedOn ? moment(new Date(campaignEmail.clickedOn)) : undefined,
    };
  }
);

export const getCampaignEmailsByIds = createSelector(
  [selectState, selectNumberIdsParam],
  (state, ids): CampaignEmail[] =>
    ids.map((id) => getCampaignEmailById(state, id)).filter(Boolean) as CampaignEmail[]
);

const selectCampaignEmailIds = createSelector(
  [selectEmailsById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getCampaignEmails = createSelector(
  [selectState, selectCampaignEmailIds],
  (state, ids) => getCampaignEmailsByIds(state, ids)
);

export const getCampaignEmailsByLeadId = createSelector(
  [getCampaignEmails, selectLeadIdParam],
  (emails, leadId) => (leadId == null ? [] : emails.filter((e) => e.leadId === leadId))
);

const selectLeadIdParam3 = (_: RootState, leadId?: number, _campaignId?: number) =>
  leadId ?? null;

const selectCampaignIdParam3 = (_: RootState, _leadId?: number, campaignId?: number) =>
  campaignId ?? null;

type EmailsByLeadAndCampaign = (state: RootState, leadId?: number, campaignId?: number) => CampaignEmail[];

export const getCampaignEmailsByLeadIdAndCampaignId: EmailsByLeadAndCampaign = createSelector(
  [getCampaignEmails, selectLeadIdParam3, selectCampaignIdParam3],
  (emails, leadId, campaignId) =>
    leadId == null || campaignId == null
      ? []
      : emails.filter(e => e.leadId === leadId && e.campaignId === campaignId)
);

export const getCampaignEmailsByCampaignId = createSelector(
  [getCampaignEmails, selectCampaignIdParam],
  (emails, campaignId) => (campaignId == null ? [] : emails.filter((e) => e.campaignId === campaignId))
);

export const getCampaignEmailsStat = (campaignEmails: CampaignEmail[]) => {
  const ids = campaignEmails.map((e) => e.id);
  const leadIds = campaignEmails.map((e) => e.leadId);
  const total = campaignEmails.length;

  const pending = campaignEmails.filter((e) => e.status.pending).length;
  const failures = campaignEmails.filter((e) => e.status.failed).length;
  const successes = campaignEmails.filter((e) => e.status.success).length;

  const opens = campaignEmails.filter((e) => !!e.openedOn).length;
  const clicks = campaignEmails.filter((e) => !!e.clickedOn).length;

  const clickCount = campaignEmails.reduce((sum, e) => sum + (e.clicks || 0), 0);

  const successRate = total ? successes / total : 0;
  const failureRate = total ? failures / total : 0;
  const openRate = total ? opens / total : 0;
  const clickRate = total ? clicks / total : 0;

  return {
    ids,
    leadIds,
    total,
    pending,
    failures,
    successes,
    opens,
    clicks,
    clickCount,
    successRate,
    failureRate,
    openRate,
    clickRate,
  };
};

//campaignSubscriptionStats

export const getCampaignSubscriptionStatById = (state: RootState, id?: number): CampaignSubscriptionStat | undefined => {
	if (id) {
		const campaignSubscriptionStat = state.campaignSubscriptionStats.byId[id];
		const lastSent = campaignSubscriptionStat.lastSent ? moment(new Date(campaignSubscriptionStat.lastSent)) : undefined;
		const campaign = getCampaignById(state, campaignSubscriptionStat.campaignId);
		const lead = getLeadById(state, campaignSubscriptionStat.leadId);
		const campaignSubscription = getCampaignSubscriptionById(state, campaignSubscriptionStat.campaignSubscriptionId);
		const messageStatusDate = campaignSubscriptionStat.messageStatusDate ? moment(new Date(campaignSubscriptionStat.messageStatusDate)) : undefined;
		const messageStatus = campaignSubscriptionStat.messageStatus;
		if (campaignSubscriptionStat && campaign && lead && campaignSubscription) {
			return {
				...campaignSubscriptionStat, 
				messageStatusDate,
				messageStatus,
				lastSent: lastSent,
				campaign,
				lead,
				campaignSubscription,
			};
		}
	}
};

export const getCampaignSubscriptionStatByCampaignSubscriptionId = (state: RootState, campaignSubscriptionId: number): CampaignSubscriptionStat | undefined => {
	return getSubscriptionStats(state).find((campaignSubscription) => campaignSubscription.campaignSubscription.id === campaignSubscriptionId);
}

export const getSubscriptionStats = createSelector(
	(state: RootState) => state.campaignSubscriptionStats.byId,
	(state: RootState) => state,
	(byId, state) => {
		return Object.keys(byId).map((id) => {
			return getCampaignSubscriptionStatById(state, parseInt(id));
		}).filter(Boolean) as CampaignSubscriptionStat[];
	}
);

// CampaignStats 

export const getCampaignStatById = (state: RootState, id?: number): CampaignStat | undefined => {
	if (id) {
		const campaignStat = state.campaignStats.byCampaignId[id];
		if (campaignStat) {
			return {
				...campaignStat,
			};
		}
	}
};

export const getCampaignStats = createSelector(
	(state: RootState) => state.campaignStats.byCampaignId,
	(state: RootState) => state,
	(byCampaignId, state) => {
		return Object.keys(byCampaignId).map((id) => {
			return getCampaignStatById(state, parseInt(id));
		}).filter(Boolean) as CampaignStat[];
	}
);

export const getCampaignStatsByCampaignId = (state: RootState, campaignId: number) => {
	const campaignStats = getCampaignStats(state).find((campaignStat) => {
		return campaignStat.campaignId === campaignId;
	});
	return campaignStats;
}

// CampaignSettings 

export const getCampaignsSettings = createSelector(
	(state: RootState) => state.campaignsSettings,
	(state: RootState) => state,
	(campaignsSettingsState, state): CampaignsSettings | undefined => {
		if (campaignsSettingsState === undefined) {
			return;
		}
		if (campaignsSettingsState) {
			const lead = getLeadById(state, campaignsSettingsState.leadId);
			return {
				dynamicContentAddress: campaignsSettingsState.dynamicContentAddress,
				lead: lead,
			};
		}
	}
);

// CampaignRoutes 

export const getCampaignRouteById = (state: RootState, id?: number): CampaignRoute | undefined => {
	if (id) {
		const campaignRoute = state.campaignRoutes.byId[id];
		if (campaignRoute) {
			// MAX-3857: treat an inactive campaign as missing -- getCampaignById doesn't filter
			// inactiveYn, so the cached campaign would otherwise keep resolving after delete until a
			// refetch excluded it, delaying the "No Campaign" update until a manual refresh.
			const rawCampaign = getCampaignById(state, campaignRoute.campaignId);
			const campaign = rawCampaign && !rawCampaign.inactiveYn ? rawCampaign : undefined;
			// Backward compat: pre-migration routes have triggers:[] but leadSource set.
			// Synthesize triggers from leadSource so they display correctly during transition.
			const triggers = campaignRoute.triggers?.length > 0
				? campaignRoute.triggers
				: campaignRoute.leadSource ? [campaignRoute.leadSource] : [];
			return {
				...campaignRoute,
				triggers,
				campaign,
			};
		}
	}
};

export const getCampaignRoutes = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.campaignRoutes.byId).map((id) => parseInt(id));
		return ids.map((id) => getCampaignRouteById(state, id)).filter(Boolean) as CampaignRoute[];
	}
);

// CampaignMessages

export const getCampaignMessageById = (state: RootState, id?: number): CampaignMessage | undefined => {
	if (id) {
		const campaignMessage = state.campaignMessages.byId[id];
		if (campaignMessage && !campaignMessage.isInactiveYn) {
			const status = CampaignMessageStatus.getById(campaignMessage.status);
			const campaignMessageType = CampaignMessageType.getById(campaignMessage.messageType);
			return {
				...campaignMessage,
				messageType: campaignMessageType ? campaignMessageType : CampaignMessageType.EMAIL,
				dateCreated: moment(new Date(campaignMessage.dateCreated)),
				dateModified: campaignMessage.dateModified ? moment(new Date(campaignMessage.dateModified)) : undefined,
				timeToSendUnit: TimeUnitType.getById(campaignMessage.timeToSendUnit),
				status: status ? status : CampaignMessageStatus.ACTIVE,
				attachments: campaignMessage.attachments,
			};
		}
	}
};

export const getCampaignMessagesByIds = createSelector(
	(state: RootState, ids?: number[]) => state,
	(state: RootState, ids?: number[]) => ids || [],
	(state, ids) => {
		return ids.map((id) => getCampaignMessageById(state, id)).filter(Boolean) as CampaignMessage[];
	}
);

export const getCampaignMessages = createSelector(
	(state: RootState) => state.campaignMessages.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId).map((id) => parseInt(id));
		return getCampaignMessagesByIds(state, ids); 
	}
);

// CampaignCampaignMessageStats

const selectMsgStatsById = (s: RootState) => s.campaignCampaignMessageStats.byCampaignCampaignMessageId;

export const getCampaignCampaignMessageStatById = createSelector(
  [selectMsgStatsById, selectIdParam],
  (byId, id): CampaignCampaignMessageStat | undefined => {
    if (id == null) return undefined;
    const stat = byId[id];
    return stat ? { ...stat } : undefined;
  }
);

export const getCampaignCampaignMessageStats = createSelector(
  [selectMsgStatsById],
  (byId): CampaignCampaignMessageStat[] =>
    Object.keys(byId)
      .map(k => {
        const stat = byId[parseInt(k, 10)];
        return stat ? { ...stat } : undefined;
      })
      .filter(Boolean) as CampaignCampaignMessageStat[]
);

export const getCampaignCampaignMessageStatsByCampaignCampaignMessageId = createSelector(
  [selectMsgStatsById, selectIdParam],
  (byId, id): CampaignCampaignMessageStat | undefined => {
    if (id == null) return undefined;
    const stat = byId[id];
    return stat ? { ...stat } : undefined;
  }
);

// CampaignCampaignMessages

const selectCCMById = (s: RootState) => s.campaignCampaignMessages.byId;
const selectCCMIdParam = (_: RootState, id?: number) => (id ?? null);
const selectCampaignMessageIdParam = (_: RootState, id?: number) => (id ?? null);


const selectCCMIds = createSelector(
  [selectCCMById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getCampaignCampaignMessageById = createSelector(
  [selectCCMById, selectState, selectCCMIdParam],
  (byId, state, id): CampaignCampaignMessage | undefined => {
    if (id == null) return undefined;
    const ccm = byId[id];
    if (!ccm) return undefined;

    const campaignMessage = getCampaignMessageById(state, ccm.campaignMessageId);
    if (!campaignMessage) return undefined;

    const stats =
      getCampaignCampaignMessageStatsByCampaignCampaignMessageId(state, ccm.id);

    return {
      ...ccm,
      campaignMessage,
      dateCreated: moment(new Date(ccm.dateCreated)),
      stats,
    };
  }
);

export const getCampaignCampaignMessages = createSelector(
  [selectState, selectCCMIds],
  (state, ids): CampaignCampaignMessage[] => {
    const list = ids
      .map((id) => getCampaignCampaignMessageById(state, id))
      .filter(Boolean) as CampaignCampaignMessage[];

    return [...list].sort((a, b) => {
      if (a.messageOrder < b.messageOrder) return -1;
      if (a.messageOrder > b.messageOrder) return 1;
      return 0;
    });
  }
);

export const getCampaignCampaignMessagesByCampaignMessageId = createSelector(
  [getCampaignCampaignMessages, selectCampaignMessageIdParam],
  (list, campaignMessageId) =>
    campaignMessageId == null ? [] : list.filter(m => m.campaignMessageId === campaignMessageId)
);

export const getCampaignCampaignMessagesByCampaignId = createSelector(
  [getCampaignCampaignMessages, selectCampaignIdParam],
  (list, campaignId) =>
    campaignId == null ? [] : list.filter(m => m.campaignId === campaignId)
);

export const getCampaignCampaignMessagesCount = createSelector(
  [getCampaignCampaignMessages, selectCampaignIdParam],
  (list, campaignId) => (campaignId == null ? 0 : list.filter(m => m.campaignId === campaignId).length)
);

// Cities

const selectCitiesById = (s: RootState) => s.cities.byId;

export const getCityById = createSelector(
  [selectCitiesById, selectIdParam],
  (citiesById, id): City | undefined => {
    if (id == null) return undefined;
    const city = citiesById[id];
    return city ? { ...city } : undefined;
  }
);

export const getCitiesByIds = createSelector(
  [selectCitiesById, selectNumberIdsParam],
  (citiesById, ids): City[] =>
    ids.map((id) => citiesById[id]).filter(Boolean) as City[]
);

const selectCityIds = createSelector(
  [selectCitiesById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getCities = createSelector(
  [selectCitiesById, selectCityIds],
  (citiesById, ids) => ids.map((id) => citiesById[id]).filter(Boolean) as City[]
);

// Clients

export const getClientById = (state: RootState, id?: number ): Client | undefined => {
	if (id) {
		const client = state.clients.byId[id];
		if (client) {
			return {
				...client,
			};
		}
	}
}

// EmailClicks

export const getEmailClickById = (state: RootState, id?: number): EmailClick | undefined => {
	if (id) {
		const emailClick = state.emailClicks.byId[id];
		if (emailClick) {
			const listing = getListingById(state, emailClick.listing);
			return {
				...emailClick,
				clickedOn: moment(new Date(emailClick.clickedOn)),
				listing,
			}
		}
	}
};

export const getEmailClicksByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getEmailClickById(state, id)).filter(Boolean) as EmailClick[];
};

export const getEmailClicks = createSelector(
	(state: RootState) => state.emailClicks.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId).map((id) => parseInt(id));
		return getEmailClicksByIds(state, ids);
	}
);

export const getEmailClicksForSavedSearch = (state: RootState, savedSearchId: number) => {
	const ids = state.emailClicks.bySavedSearchId[savedSearchId];
	return getEmailClicksByIds(state, ids);
};

const getEmailClicksForLead = (state: RootState, leadId: number) => {
	const ids = state.emailClicks.byLeadId[leadId];
	return getEmailClicksByIds(state, ids);
};

export const getEmailClicksForLeadAndMarketReport = (state: RootState, leadId: number, marketReportId: number) => {
	return getEmailClicksForLead(state, leadId).filter((emailClick) => emailClick.marketReportId === marketReportId);
};

//EmailSents 

export const getEmailSentById = (state: RootState, id?: string): EmailSent | undefined => {
	if (id) {
		const emailSent = state.emailSents.byId[id];
		if (emailSent) {
			return {
				...emailSent,
				sentOn: moment(new Date(emailSent.sentOn)),
			}
		}
	}
};

export const getEmailSentsByIds = (state: RootState, ids?: string[]) => {
	ids = ids || [];
	return ids.map((id) => getEmailSentById(state, id)).filter(Boolean) as EmailSent[];
};

export const getEmailSents = createSelector(
	(state: RootState) => state.emailSents.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId);
		return getEmailSentsByIds(state, ids); 
	}
);

export const getEmailSentsForSavedSearch = (state: RootState, savedSearchId: number) => {
	const ids = state.emailSents.bySavedSearchId[savedSearchId];
	return getEmailSentsByIds(state, ids);
};

export const getEmailSentsForLead = (state: RootState, leadId: number) => {
	const ids = state.emailSents.byLeadId[leadId];
	return getEmailSentsByIds(state, ids);;
};

export const getEmailSentsForLeadAndMarketReport = (state: RootState, leadId: number, marketReportId: number) => {
	return getEmailSentsForLead(state, leadId).filter((emailSent) => emailSent.marketReportId === marketReportId);
};

// IntegrationCalendar

export const getIntegrationCalendarById = (state: RootState, id: string): IntegrationCalendar | undefined => {
	if (id) {
		const integrationCalendar = state.integrationCalendars.byId[id];
		if (integrationCalendar) {
			return {
				...integrationCalendar,
			}
		}
	}
};

export const getIntegrationCalendarsByIds = (state: RootState, ids?: string[]) => {
	ids = ids || [];
	return ids.map((id) => getIntegrationCalendarById(state, id)).filter(Boolean) as IntegrationCalendar[];
};

export const getIntegrationCalendars = createSelector(
	(state: RootState) => state.integrationCalendars.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId);
		return getIntegrationCalendarsByIds(state, ids);
	}
);

export const getIntegrationCalendarsByIntegrationId = (state: RootState, integrationId: number) => {
	return getIntegrationCalendars(state).filter(integrationCalendar => integrationCalendar.integrationId === integrationId);
};

// Integrations

export const getIntegrationById = (state: RootState, id: number): Integration | undefined => {
	if (id) {
		const integration = state.integrations.byId[id];
		if (integration) {
			const integrationType = IntegrationType.getById(integration.integrationTypeId);
			if (integrationType) {
				return {
					...integration,
					data: {
						...integration.data,
					},
					integrationType,
					createdOn: moment(new Date(integration.createdOn)),
					label: integration.name || integrationType.name,
				}
						}
		}
	}
};

export const getIntegrationsByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getIntegrationById(state, id)).filter(Boolean) as Integration[];
};

export const getIntegrations = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.integrations.byId).map((id) => parseInt(id));
		return getIntegrationsByIds(state, ids); 
	}
);

export const getIntegrationsNewestToOldest = createSelector(
	(state: RootState) => state,
	(state) => {
		return getIntegrations(state).sort((a, b) => {
			return b.createdOn.valueOf() - a.createdOn.valueOf();
		});
	}
);

export const getIntegrationByType = (state: RootState, integrationType: IntegrationType) => {
	return getIntegrations(state).find(integration => integration.integrationType === integrationType);
};

export const getIntegrationByTypeId = (state: RootState, integrationType: IntegrationType) => {
	return getIntegrations(state).find(integration => integration.integrationType.id === integrationType.id);
  };

export const missingEmailCalendarIntegration = createSelector(
	(state: RootState) => state,
	(state) => {
		return !state.integrations.loading && !getIntegrationByType(state, IntegrationType.EMAIL_CALENDAR);
	}
);

// Languages

export const getLanguageById = (state: RootState, id: number): Language | undefined => {
	if (id) {
		const language = state.languages.byId[id];
		if (language) {
			return {
				...language,
			}
		}
	}
};

export const getLanguagesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getLanguageById(state, id)).filter(Boolean) as Language[];
};

export const getLanguages = createSelector(
	(state: RootState) => state.languages.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId).map((id) => parseInt(id));
		return getLanguagesByIds(state, ids);
	}
);

// LeadActivities

export const getLeadActivityById = (state: RootState, id?: string): LeadActivity | undefined => {
	if (!id || !state.leadActivities.byId[id]) {
        return undefined;
    }
	
	if (id) {
		const leadActivity = state.leadActivities.byId[id];
		if (leadActivity) {
			const type = LeadActivityType.getById(leadActivity.typeId);
			const listing = getListingById(state, leadActivity.listing) || undefined;
			let textMessageActivity: TextMessageActivity | undefined;
			if (leadActivity.textMessageActivity) {
				textMessageActivity = {
					...leadActivity.textMessageActivity,
					dateSent: moment(new Date(leadActivity.textMessageActivity.dateSent)),	
				}
			}
			if (type) {
				return {
					...leadActivity,
					createdOn: moment(new Date(leadActivity.createdOn)),
					type,
					listing,
					textMessageActivity,
					aiSessionId: leadActivity.aiSessionId ?? null,
					viaSearchAgent: leadActivity.viaSearchAgent === true,
				};
			}
		}
	}
};

export const getLeadActivitiesByIds = (state: RootState, ids?: string[]) => {
	ids = ids || [];
	return ids.map((id) => getLeadActivityById(state, id)).filter(Boolean) as LeadActivity[];
};

export const getLeadActivities = createSelector(
	(state: RootState) => state.leadActivities.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId);
		const activities = getLeadActivitiesByIds(state, ids);
		
		// Filter out duplicates based on leadId, type, listing id, and createdOn within 10 seconds
		const filteredActivities: LeadActivity[] = [];
		
		// Sort by createdOn descending to keep the most recent activity in case of duplicates
		const sortedActivities = [...activities].sort((a, b) => b.createdOn.valueOf() - a.createdOn.valueOf());
		
		for (const activity of sortedActivities) {
			const listingId = activity.listing?.id || 'no-listing';
			const key = `${activity.leadId}-${activity.type.id}-${listingId}`;
			
			// Check if we've seen a similar activity within the last 10 seconds
			let isDuplicate = false;
			for (const existingActivity of filteredActivities) {
				const existingListingId = existingActivity.listing?.id || 'no-listing';
				const existingKey = `${existingActivity.leadId}-${existingActivity.type.id}-${existingListingId}`;
				
				if (existingKey === key) {
					const timeDiff = Math.abs(activity.createdOn.valueOf() - existingActivity.createdOn.valueOf());
					if (timeDiff <= 10000) { // 10 seconds in milliseconds
						isDuplicate = true;
						break;
					}
				}
			}
			
			if (!isDuplicate) {
				filteredActivities.push(activity);
			}
		}
		
		return filteredActivities;
	}
);

export const getLeadActivitiesChronological = createSelector(
	(state: RootState) => state,
	(state) => {
		return getLeadActivities(state).sort((act1: LeadActivity, act2: LeadActivity) => {
			return act2.createdOn.valueOf() - act1.createdOn.valueOf()
		})
	}
);

export const getLeadActivitiesForLead = (state: RootState, leadId: number) => {
	const ids = state.leadActivities.byLeadId[leadId];
	return getLeadActivitiesByIds(state, ids);
};

export const getLeadActivitiesForLeadChronological = (state: RootState, leadId: number) => {
	return getLeadActivitiesForLead(state, leadId).sort((act1: LeadActivity, act2: LeadActivity) => {
		return act2.createdOn.valueOf() - act1.createdOn.valueOf()
	})
};

// LeadActivityStats 

export const getLeadActivityStatById = (state: RootState, id?: string): LeadActivityStat | undefined => {
	if (id) {
		const leadActivityStat = state.leadActivityStats.byId[id];
		if (leadActivityStat) {
			return {
				...leadActivityStat,
			};	
		}
	}
};

export const getLeadActivityStatsByIds = (state: RootState, ids?: string[]) => {
	ids = ids || [];
	return ids.map((id) => getLeadActivityStatById(state, id)).filter(Boolean) as LeadActivityStat[];
};

export const getLeadActivityStats = createSelector(
	(state: RootState) => state.leadActivityStats.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId);
		return getLeadActivityStatsByIds(state, ids);
	}
);

// LeadAnniversaries

export const getLeadAnniversaryById = (state: RootState, id?: number): LeadAnniversary | undefined => {
	if (id) {
		const leadAnniversary = state.leadAnniversaries.byId[id];
		if (leadAnniversary) {
			const type = LeadAnniversaryType.getById(leadAnniversary.typeId);
			const lead = getLeadById(state, leadAnniversary.leadId);
			if (type && lead) {
				let upcomingAnniversary;
				const anniversaryThisYear = moment(new Date(leadAnniversary.startDate)).set({"y": moment().year(), "hour": 23, "minute": 59});
				if (anniversaryThisYear < moment()) {
					upcomingAnniversary = anniversaryThisYear.add(1, "y");
				} else {
					upcomingAnniversary = anniversaryThisYear;
				}
				return {
					...leadAnniversary,
					type,
					lead,
					upcomingAnniversary, 
					updatedOn: moment(new Date(leadAnniversary.updatedOn)),
					startDate: moment(new Date(leadAnniversary.startDate)),
					createdOn: moment(new Date(leadAnniversary.createdOn)),
				};
			}
		}
	}
};

export const getLeadAnniversariesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getLeadAnniversaryById(state, id)).filter(Boolean) as LeadAnniversary[];
};

export const getLeadAnniversaries = createSelector(
	(state: RootState) => state.leadAnniversaries.byId,
	(state: RootState) => state,
	(byId, state) => {
		const ids = Object.keys(byId).map((id) => parseInt(id));
		return getLeadAnniversariesByIds(state, ids); 
	}
);

export const getChronologicalLeadAnniversaries = createSelector(
	(state: RootState) => state,
	(state) => {
		const leadAnniversaries = getLeadAnniversaries(state);
		if (leadAnniversaries.length > 1) {
			leadAnniversaries.sort((a,b) => {
				return a.upcomingAnniversary.valueOf() - b.upcomingAnniversary.valueOf();
			})
		}
		return leadAnniversaries;
	}
);

export const getUpcommingLeadAnniversaries = createSelector(
	(state: RootState) => state,
	(state) => {
		return getChronologicalLeadAnniversaries(state).filter(leadAnniversary => {
			const { upcomingAnniversary } = leadAnniversary;
			const difference = moment.duration(upcomingAnniversary.diff(moment())).asDays();
			return difference < 7 && difference > 0;
		});
	}
);

export const getLeadAnniversariesForLead = (state: RootState, leadId: number) => {
	const ids = state.leadAnniversaries.byLeadId[leadId];
	return getLeadAnniversariesByIds(state, ids);
};


export const getActiveLeadAnniversariesForLead = (state: RootState, leadId: number) => {
	return getLeadAnniversariesForLead(state, leadId).filter((leadAnniversary) => !leadAnniversary.deleted);
};

export const getAnniversariesForSelectedLead = (state: RootState, leadIds: number[]) => {
	return getLeadAnniversaries(state)
		.filter((leadAnniversary) => leadIds.includes(leadAnniversary.lead.id))
		.filter((leadAnniversary) => !leadAnniversary.deleted)
	;
};

// LeadImport

export const getLeadImportById = (state: RootState, id?: string): LeadImport | undefined => {
	if (id) {
		const leadImport = state.leadImports.byId[id];
		const pipelineType = LeadPipelineType.getById(leadImport.pipelineTypeId || 0);
		if (leadImport) {
			let importError: ImportError | undefined;
			if (leadImport.message === ImportError.action) {
				importError = ImportError.action;
			}
			if (leadImport.message === ImportError.import) {
				importError = ImportError.import;
			}
			if (leadImport.message === ImportError.duplicate) {
				importError = ImportError.duplicate;
			}
			let contactType: string | undefined;
			if (leadImport.tags) {
				contactType = leadImport.tags.find(tag => {
					return LeadTagType.getLeadTypeById(tag);
				});
			}
			let tags: string[] | undefined;
			if (leadImport.tags) {
				tags = leadImport.tags.filter((tag) => !LeadTagType.getById(tag));
			}
			let birthday: string | undefined;
			if (leadImport.birthday) {
				birthday = moment(leadImport.birthday).format("MM/DD/YYYY")
			}
			let transactionAnniversary: string | undefined;
			if (leadImport.transactionAnniversary) {
				transactionAnniversary = moment(leadImport.transactionAnniversary).format("MM/DD/YYYY")
			}
			return {
				id: leadImport.id,
				email: leadImport.email,
				name: leadImport.name,
				address: leadImport.address,
				state: leadImport.state,
				phone: leadImport.phone,
				city: leadImport.city,
				zip: leadImport.zip,
				birthday: birthday,
				agentId: leadImport.agentId,
				transactionAnniversary: transactionAnniversary,
				pipelineType: pipelineType ? pipelineType.label : undefined,
				contactType: contactType,
				tags: tags,
				note: leadImport.note, 
				textMessageOptIn: leadImport.textMessageOptIn,
				marketIds: leadImport.marketIds,
				campaignIds: leadImport.campaignIds,
				leadSource: leadImport.leadSource,
				error: importError,
			};
		}
	}
};

export const getLeadImportsByIds = (state: RootState, ids?: string[]) => {
	ids = ids || [];
	return ids.map((id) => getLeadImportById(state, id)).filter(Boolean) as LeadImport[];
};

export const getLeadImports = createSelector(
	// Only depend on the leadImports.byId slice so the selector
	// doesn't recompute for unrelated state updates.
	(state: RootState) => state.leadImports.byId,
	(state: RootState) => state,
	(byId, state: RootState) => {
		const ids = Object.keys(byId).map((id) => id);
		return getLeadImportsByIds(state, ids);
	}
);

export const getLeadImportsOptimized = createSelector(
	// Only depend on the specific state slices we need
	(state: RootState) => state.leadImports.byId,
	(byId) => {
		return Object.keys(byId).map((id) => {
			const leadImport = byId[id];
			if (!leadImport) return null;

			const pipelineType = LeadPipelineType.getById(leadImport.pipelineTypeId || 0);

			let importError: ImportError | undefined;
			if (leadImport.message === ImportError.action) {
				importError = ImportError.action;
			}
			if (leadImport.message === ImportError.import) {
				importError = ImportError.import;
			}
			if (leadImport.message === ImportError.duplicate) {
				importError = ImportError.duplicate;
			}

			let contactType: string | undefined;
			if (leadImport.tags) {
				contactType = leadImport.tags.find(tag => {
					return LeadTagType.getLeadTypeById(tag);
				});
			}

			let tags: string[] | undefined;
			if (leadImport.tags) {
				tags = leadImport.tags.filter((tag) => !LeadTagType.getById(tag));
			}

			let birthday: string | undefined;
			if (leadImport.birthday) {
				birthday = moment(leadImport.birthday).format("MM/DD/YYYY")
			}

			let transactionAnniversary: string | undefined;
			if (leadImport.transactionAnniversary) {
				transactionAnniversary = moment(leadImport.transactionAnniversary).format("MM/DD/YYYY")
			}

			return {
				id: leadImport.id,
				email: leadImport.email,
				name: leadImport.name,
				address: leadImport.address,
				state: leadImport.state,
				phone: leadImport.phone,
				city: leadImport.city,
				zip: leadImport.zip,
				birthday: birthday,
				agentId: leadImport.agentId,
				transactionAnniversary: transactionAnniversary,
				pipelineType: pipelineType ? pipelineType.label : undefined,
				contactType: contactType,
				tags: tags,
				note: leadImport.note, 
				textMessageOptIn: leadImport.textMessageOptIn,
				marketIds: leadImport.marketIds,
				campaignIds: leadImport.campaignIds,
				leadSource: leadImport.leadSource,
				error: importError,
			};
		}).filter(Boolean) as LeadImport[];
	}
);

// LeadLastActivities

export const getLeadLastActivityById = (state: RootState, id?: number): LeadLastActivity | undefined => {
	if (id) {
		const leadLastActivity = state.leadLastActivities.byId[id];
		if (leadLastActivity) {
			return {
				...leadLastActivity,
				timestamp: moment(new Date(leadLastActivity.timestamp)),
			};
		}
	}
};

export const getLeadLastActivitiesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getLeadLastActivityById(state, id)).filter(Boolean) as LeadLastActivity[];
};

export const getLeadLastActivities = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.leadLastActivities.byId).map((id) => parseInt(id));
		return getLeadLastActivitiesByIds(state, ids); 
	}
);

export const getLeadLastActivityForLead = (state: RootState, leadId: number) => {
	return getLeadLastActivityById(state, leadId);
};

// LeadNotes

export const getLeadNoteById = (state: RootState, id?: number): LeadNote | undefined => {
	if (id) {
		const leadNote = state.leadNotes.byId[id];
		if (leadNote) {
			const type = LeadNoteType.getById(leadNote.typeId);
			if (type) {
				return {
					...leadNote,
					type,
					updatedOn: moment(new Date(leadNote.updatedOn)),
					createdOn: moment(new Date(leadNote.createdOn)),
				};
			}
		}
	}
};

export const getLeadNotesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getLeadNoteById(state, id)).filter(Boolean) as LeadNote[];
};

export const getLeadNotes = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.leadNotes.byId).map((id) => parseInt(id));
		return getLeadNotesByIds(state, ids); 
	}
);

export const getLeadNotesForLead = (state: RootState, leadId: number) => {
	const ids = state.leadNotes.byLeadId[leadId];
	return getLeadNotesByIds(state, ids);
};

export const getActiveLeadNotesForLead = (state: RootState, leadId: number) => {
	return getLeadNotesForLead(state, leadId).filter((leadNote) => !leadNote.deleted);
};

export const getActiveLeadNotesForLeadChronological = (state: RootState, leadId: number) => {
	return getActiveLeadNotesForLead(state, leadId).sort((note1: LeadNote, note2: LeadNote) => {
		return note2.updatedOn.valueOf() - note1.updatedOn.valueOf()
	})
};

export const getCallNotes = createSelector(
	(state: RootState) => state,
	(state) => {
		return getLeadNotes(state).filter(note => note.type === LeadNoteType.PHONE_CALL);
	}
);

// LeadPhones

export const getLeadPhoneById = (state: RootState, id?: number): LeadPhone | undefined => {
	if (id) {
		const leadPhone = state.leadPhones.byId[id];
		if (leadPhone) {
			const type = LeadPhoneType.getById(leadPhone.typeId);
			return {
				...leadPhone,
				type,
				updatedOn: moment(new Date(leadPhone.updatedOn)),
				createdOn: moment(new Date(leadPhone.createdOn))
			};
		}
	}
};

export const getLeadPhonesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getLeadPhoneById(state, id)).filter(Boolean) as LeadPhone[];
};

export const getLeadPhones = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.leadPhones.byId).map((id) => parseInt(id));
		return getLeadPhonesByIds(state, ids); 
	}
);

export const getLeadPhonesForLead = (state: RootState, leadId: number) => {
	const ids = state.leadPhones.byLeadId[leadId];
	return getLeadPhonesByIds(state, ids);
};

export const getActiveLeadPhonesForLead = (state: RootState, leadId: number) => {
	return getLeadPhonesForLead(state, leadId).filter((leadPhone) => !leadPhone.deleted);
};

// LeadPipelines

export const getLeadPipelineById = (state: RootState, id?: number): LeadPipeline | undefined => {
	if (id) {
		const leadPipeline = state.leadPipelines.byId[id];
		if (leadPipeline) {
			const type = LeadPipelineType.getById(leadPipeline.typeId);
			if (type) {
				return {
					...leadPipeline,
					type,
					createdOn: moment(new Date(leadPipeline.createdOn)),
				};
			}
		}
	}
};

export const getLeadPipelinesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getLeadPipelineById(state, id)).filter(Boolean) as LeadPipeline[];
};

export const getLeadPipelines = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.leadPipelines.byId).map((id) => parseInt(id));
		return getLeadPipelinesByIds(state, ids); 
	}
);

export const getChronologicalLeadPipelinesForLead = (state : RootState, leadId: number) => {
	return getLeadPipelinesForLead(state, leadId).sort((a, b) =>
		a.createdOn < b.createdOn ? 1 : -1,
	);
};

export const getLatestLeadPipelineForLead = (state: RootState, leadId: number) => {
	return getChronologicalLeadPipelinesForLead(state, leadId).shift();
};

export const getLatestLeadPipelinesForLeads = (state: RootState, leadIds: number[]) => {
	return leadIds.map(leadId => getLatestLeadPipelineForLead(state, leadId))
};

export const getLeadPipelinesForLead = (state: RootState, leadId: number) => {
	const ids = state.leadPipelines.byLeadId[leadId];
	return getLeadPipelinesByIds(state, ids);
};

// Leads

export const getLeadById = (state: RootState, id?: number): Lead | undefined => {
    if (!id || !state.leads.byId[id]) {
        return undefined;
    }
	if (id) {
		const lead = state.leads.byId[id];
		if (lead) {
			const status = LeadStatus.getById(lead.status);
			const agent = getAgentById(state, lead.agentId);
			let lastActiveOn: Moment | undefined;
			{
				const leadLastActivity = getLeadLastActivityForLead(state, lead.id);
				if (leadLastActivity) {
					lastActiveOn = leadLastActivity.timestamp;
				}
			}
			const lastSearchAgentChat = state.leadLastSearchAgentChats.byId[lead.id];
			const lastSearchAgentChatOn = lastSearchAgentChat ? moment(new Date(lastSearchAgentChat.timestamp)) : undefined;
			const pipeline = getLatestLeadPipelineForLead(state, lead.id);
			if (status && lead.createdOn) {
				return {
					...lead,
					label: lead.name && lead.name.trim() ? lead.name.trim() : lead.email,
					createdOn: moment(new Date(lead.createdOn)),
					lastActiveOn,
					lastSearchAgentChatOn,
					agent,
					status,
					get subscribed() {
						let subscribed = false;
						if (!subscribed) {
							subscribed = getActiveSavedSearchesForLead(state, lead.id).length > 0;
						}
						if (!subscribed) {
							subscribed = getActiveMarketReportSubscriptionsForLead(state, lead.id).length > 0;
						}
						if (!subscribed) {
							subscribed = getActiveDripCampaignSubscriptionsForLead(state, lead.id).length > 0;
						}
						return subscribed;
					},
					pipeline,
				};
			}
		}
	}
};

export const getLeadsByIds = createSelector(
	(state: RootState, ids?: number[]) => ({ state, ids: ids || [] }),
	({ state, ids }): Lead[] => {
		return ids.map((id) => getLeadById(state, id)).filter(Boolean) as Lead[];
	}
);

// export const getLeads = createSelector(
//     (state: RootState) => state,
//     (state) => {
//         const ids = Object.keys(state.leads.byId).map((id) => parseInt(id));
//         return getLeadsByIds(state, ids);
//     }
// );

// Optimized version for Base component to avoid re-render issues
type LeadCache = {
  // dependency refs for quick referential checks
  leadRef: any;
  agentRef: any;
  lastActRef: any;
  lastSearchAgentChatRef: any;
  pipelineIdsRef: any;
  pipelinesByIdRef: any;
  savedRef: any;
  marketRef: any;
  campaignRef: any;
  // final computed value to reuse
  value: Lead;
};

export const getLeadsOptimized = (() => {
  const perIdCache = new Map<number, LeadCache>();
  let prevResult: Lead[] = [];

  return createSelector(
    (state: RootState) => state.leads.byId,
    (state: RootState) => state.agents.byId,
    (state: RootState) => state.leadLastActivities.byId,
    (state: RootState) => state.leadLastSearchAgentChats.byId,
    (state: RootState) => state.leadPipelines.byLeadId,
    (state: RootState) => state.leadPipelines.byId,
    (state: RootState) => state.savedSearches.byLeadId,
    (state: RootState) => state.marketReportSubscriptions.byLeadId,
    (state: RootState) => state.campaignSubscriptions.byLeadId,
    (
      leadsById,
      agentsById,
      leadLastActivitiesById,
      leadLastSearchAgentChatsById,
      leadPipelinesByLeadId,
      leadPipelinesById,
      savedSearchesByLeadId,
      marketReportSubscriptionsByLeadId,
      campaignSubscriptionsByLeadId
    ) => {
      const ids = Object.keys(leadsById).map((id) => parseInt(id, 10));

      let anyChanged = false;
      const next: Lead[] = new Array(ids.length);

      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        const lead = leadsById[id];
        if (!lead) continue;

        const status = LeadStatus.getById(lead.status);
        const agent = lead.agentId ? agentsById[lead.agentId] : undefined;

        // Dependencies to check for this lead
        const lastAct = leadLastActivitiesById[id];
        const lastSearchAgentChat = leadLastSearchAgentChatsById[id];
        const pipelineIds = leadPipelinesByLeadId[id] ?? EMPTY;
        const saved = savedSearchesByLeadId[id] ?? EMPTY;
        const market = marketReportSubscriptionsByLeadId[id] ?? EMPTY;
        const campaign = campaignSubscriptionsByLeadId[id] ?? EMPTY;

        const prev = perIdCache.get(id);
        const depsStable =
          prev &&
          prev.leadRef === lead &&
          prev.agentRef === agent &&
          prev.lastActRef === lastAct &&
          prev.lastSearchAgentChatRef === lastSearchAgentChat &&
          prev.pipelineIdsRef === pipelineIds &&
          prev.pipelinesByIdRef === leadPipelinesById &&
          prev.savedRef === saved &&
          prev.marketRef === market &&
          prev.campaignRef === campaign;

        if (depsStable) {
          // Reuse exact same object reference
          next[i] = prev.value;
          continue;
        }

        anyChanged = true;

        // Build lastActiveOn
        const lastActiveOn = lastAct ? moment(new Date(lastAct.timestamp)) : undefined;
        const lastSearchAgentChatOn = lastSearchAgentChat ? moment(new Date(lastSearchAgentChat.timestamp)) : undefined;

        // Build latest pipeline
        let pipeline: LeadPipeline | undefined;
        if (pipelineIds.length > 0) {
          const pipelines = pipelineIds
            .map((pipelineId) => leadPipelinesById[pipelineId])
            .filter(Boolean)
            .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());

          if (pipelines.length > 0) {
            const pipelineData = pipelines[0];
            const pipelineType = LeadPipelineType.getById(pipelineData.typeId);
            if (pipelineType) {
              pipeline = {
                ...pipelineData,
                type: pipelineType,
                createdOn: moment(new Date(pipelineData.createdOn)),
              } as LeadPipeline;
            }
          }
        }

        // Build agent object (no office here, as you had)
        let agentObject: Agent | undefined;
        if (agent) {
          let name = '';
          if (agent.firstName) {
            name += agent.firstName;
            if (agent.lastName) {
              name += ' ';
              name += agent.lastName;
            }
          }
          let label = agent.email ?? '';
          if (name) label = name;

          agentObject = {
            ...agent,
            name,
            label,
            lastLoginDate: agent.lastLoginDate ? moment(new Date(agent.lastLoginDate)) : undefined,
            office: undefined,
            staffType: AgentStaffType.getById(agent.staffTypeId),
            languageIds: agent.languageIds || [],
            admin: !!agent.admin,
          };
        }

        if (status && lead.createdOn) {
          // subscribed flag (no getters)
          const subscribed =
            (saved as number[]).length > 0 ||
            (market as number[]).length > 0 ||
            (campaign as number[]).length > 0;

          const built: Lead = {
            ...lead,
            label: lead.name && lead.name.trim() ? lead.name.trim() : lead.email,
            createdOn: moment(new Date(lead.createdOn)),
            lastActiveOn,
            lastSearchAgentChatOn,
            agent: agentObject,
            status,
            subscribed,
            pipeline,
          } as Lead;

          // Update per‑id cache
          perIdCache.set(id, {
            leadRef: lead,
            agentRef: agent,
            lastActRef: lastAct,
            lastSearchAgentChatRef: lastSearchAgentChat,
            pipelineIdsRef: pipelineIds,
            pipelinesByIdRef: leadPipelinesById,
            savedRef: saved,
            marketRef: market,
            campaignRef: campaign,
            value: built,
          });

          next[i] = built;
        }
      }

      // If nothing changed (all items reused and same order), return the exact same array ref.
      if (!anyChanged && prevResult.length === next.length) {
        let allSame = true;
        for (let i = 0; i < next.length; i++) {
          if (prevResult[i] !== next[i]) {
            allSame = false;
            break;
          }
        }
        if (allSame) return prevResult;
      }

      prevResult = next;
      return next;
    }
  );
})();

export const getActiveLeads = createSelector(
	(state: RootState) => state,
	(state) => {
		return getLeadsOptimized(state).filter(lead => lead.status.active);
	}
);

export const getActiveLeadsByAgentId = (state: RootState, agentId: number) => {
		return getLeadsOptimized(state).filter(lead => lead.status.active && lead.agent?.id === agentId);
	};

export const getLeadsById = (state: RootState, leadIds: number[]) => {
	return getLeadsOptimized(state).filter(lead => leadIds.includes(lead.id));
};

export const getLeadsByAgentId = (state: RootState, agentId: number) => {
	return getLeadsOptimized(state).filter(lead => lead.agent?.id === agentId);
};

export const getLeadsByUnassignedAgent = createSelector(
	(state: RootState) => getLeadsOptimized(state),
	(leads) => leads.filter(lead => !lead.agent)
);

export const getActiveLeadsWithinMonth = createSelector(
	(state: RootState) => state,
	(state) => {
		return getActiveLeads(state).filter(lead => lead.createdOn > moment().subtract(1, "M"));
	}
);

export const getFilteredLeads = createSelector(
	(state: RootState) => state,
	(state) => {
		let leads = getLeadsOptimized(state);
		const filters = getLeadsPageFilters(state);
		{
			const values = filters.agent;
			if (values && values.length) {
				leads = leads.filter((lead) => {
					return values.some((value) => {
						if (lead.agent) {
							return lead.agent.id === value;
						}
						if (value === -1) {
							return !lead.agent;
						}
					});
				});
			}
		}
		{
			const values = filters.type;
			if (values && values.length) {
				leads = leads.filter((lead) => {
					return values.some((value) => {
						return getLeadTagByText(state, lead.id, value);
					});
				});
			}
		}
		{
			const values = filters.status;
			if (values && values.length) {
				leads = leads.filter((lead) => {
					return values.some((value) => {
						return lead.status.id === value;
					});
				});
			}
		}
		{
			const value = filters.bookmarked;
			if (value) {
				leads = leads.filter((lead) => {
					return getBookmarkTagForLead(state, lead.id);
				});
			}
		}
		{
			const value = filters.subscribed;
			if (value) {
				leads = leads.filter((lead) => {
					return lead.subscribed;
				});
			}
		}
		{
			const value = filters.searchAgent;
			if (value && hasSearchAgent(getUserUnsafe(state))) {
				leads = leads.filter((lead) => {
					return !!lead.lastSearchAgentChatOn;
				});
			}
		}
		{
			const values = filters.ranking;
			if (values && values.length) {
				leads = leads.filter((lead) => {
					let ranking = LeadRanking.NO_RANKING;
					let allLeads = getActiveLeads(state).sort(function(lead1, lead2) {
						return (lead1.ranking || 0) > (lead2.ranking || 0) ? -1 : 1;
					});
					let rating = 0;
					let ratingSize = 0;
					allLeads.forEach(function(lead) {
						if (lead.ranking && lead.ranking > 0) {
							ratingSize = ratingSize + 1;
						}
					});
					allLeads.forEach(function(lead2) {
						rating++;
						if (lead.id === lead2.id && lead2.ranking && lead2.ranking > 0) {
							if (rating > (ratingSize / 3) * 2) {
								ranking = LeadRanking.LOW;
							} else if (rating > ratingSize / 3) {
								ranking = LeadRanking.MEDIUM;
							} else {
								ranking = LeadRanking.HIGH;
							}
						}
					});
					return values.some((value) => {
						return ranking.id === value;
					});
				});
			}
		}
		{
			const values = filters.source;
			if (values && values.length) {
				leads = leads.filter((lead) => {
					return values.some((value) => {
						return lead.source === value;
					});
				});
			}
		}
		{
			const values = filters.pipeline;
			if (values && values.length) {
				leads = leads.filter((lead) => {
					return values.some((value) => {
						return lead.pipeline && lead.pipeline.type.id === value;
					});
				});
			}
		}
		{
			const values = filters.tag;
			if (values && values.length) {
				leads = leads.filter((lead) => {
					return values.some((value) => {
						return getLeadTagByText(state, lead.id, value);
					});
				});
			}
		}
		return leads;
	}
);

export const getActiveFilteredLeads = createSelector(
	(state: RootState) => state, 
	(state) => {
		return getFilteredLeads(state).filter((lead: Lead) => lead.status === LeadStatus.ACTIVE);
	}
);

export const getLeadsSources = createSelector(
	(state: RootState) => state,
	(state) => {
		let leadSources: string[] = [];
		getLeadsOptimized(state).forEach((lead) => {
			if (lead.source) {
				const isAdded = leadSources.some((source) => source === lead.source);
				if (!isAdded) {
					leadSources.push(lead.source);
				}
			}
		});
		return leadSources;
	}
);



export const getActiveLeadEmails = createSelector(
	(state: RootState) => state, 
	(state) => {
		return getLeadsOptimized(state).filter((lead: Lead) => lead.status === LeadStatus.ACTIVE).map((lead: Lead) => lead.email);
	}
);

export const getNonActiveLeadEmails = createSelector(
	(state: RootState) => state, 
	(state) => {
		return getLeadsOptimized(state).filter((lead: Lead) => lead.status !== LeadStatus.ACTIVE).map((lead: Lead) => lead.email);
	}
);

// LeadsPage

export const getLeadsPageFilters = (state: RootState): LeadsPageFilters => {
	return state.leadsPage.filters;
}

export const getLeadsPageFiltersCount = (state: RootState) => {
	return countLeadsPageFilters(getLeadsPageFilters(state));
}

// a constant so the default does not break memoization of selectors built on the sort
const DEFAULT_LEADS_PAGE_SORT: Sort = {
	id: "createdOn",
	desc: true,
};

export const getLeadsPageSort = (state: RootState): Sort => {
	return state.leadsPage.sort || DEFAULT_LEADS_PAGE_SORT;
}

export const getLeadsPagePageSize = (state: RootState): number => {
	return state.leadsPage.pageSize;
}

export const getLeadsPagePage = (state: RootState): number => {
	return state.leadsPage.page;
}

export const getLeadsPageColumns = (state: RootState): LeadColumnType[] => {
	return state.leadsPage.columnTypeIds.map((id) => {
		return LeadColumnType.getById(id);
	}).filter(Boolean) as LeadColumnType[];
}

export const getLeadsPageSearchTerm = (state: RootState): string => {
	return state.leadsPageSearch.term;
}

// getFilteredLeads recomputes on every state change; this returns the previous array while its
// leads are unchanged so selectors built on it stay memoized
const getFilteredLeadsStable = (() => {
	let prev: Lead[] = [];
	return (state: RootState) => {
		const next = getFilteredLeads(state);
		if (next.length === prev.length && next.every((lead, index) => lead === prev[index])) {
			return prev;
		}
		prev = next;
		return next;
	};
})();

const selectLeadsPageColumnTypeIds = (state: RootState) => state.leadsPage.columnTypeIds;

// the Lead List's filtered, searched and sorted lead ids, in display order
export const getLeadsPageLeadIds = createSelector(
	[getFilteredLeadsStable, getLeadsPageSearchTerm, getLeadsPageSort, getUserUnsafe, selectLeadsPageColumnTypeIds],
	(leads, term, sort, user, columnTypeIds): number[] => {
		if (!user) {
			return [];
		}
		const savedColumnTypes = columnTypeIds
			.map((id) => LeadColumnType.getById(id))
			.filter(Boolean) as LeadColumnType[];
		const columnTypes = getLeadsPageColumnTypes(user, savedColumnTypes);
		return sortLeads(searchLeads(leads, term), sort, user, columnTypes).map((lead) => lead.id);
	}
);

// LeadTags

/* stable inputs */
const selectLeadTagsById = (s: RootState) => s.leadTags.byId;
const selectLeadTagsByLeadId = (s: RootState) => s.leadTags.byLeadId;

export const getTagById = createSelector(
  [selectLeadTagsById, selectIdParam],
  (byId, id): LeadTag | undefined => {
    if (id == null) return undefined;
    const tag = byId[id];
    if (!tag) return undefined;

    const defaultType = LeadTagType.getById(tag.text);

    return {
      ...tag,
      defaultType,
      createdOn: moment(new Date(tag.createdOn)),
      updatedOn: moment(new Date(tag.updatedOn)),
    };
  }
);

export const getTagsByIds = createSelector(
  [selectState, selectNumberIdsParam],
  (state, ids): LeadTag[] =>
    ids.map((id) => getTagById(state, id)).filter(Boolean) as LeadTag[]
);

const selectTagIds = createSelector(
  [selectLeadTagsById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getTags = createSelector(
  [selectState, selectTagIds],
  (state, ids) => getTagsByIds(state, ids)
);

// Optimized version that doesn't depend on entire state
export const getTagsOptimized = createSelector(
  [selectLeadTagsById],
  (byId): LeadTag[] => {
    const ids = Object.keys(byId).map((k) => parseInt(k, 10));
    return ids.map((id) => {
      const tag = byId[id];
      if (!tag) return undefined;
      
      const defaultType = LeadTagType.getById(tag.text);
      return {
        ...tag,
        defaultType,
        createdOn: moment(new Date(tag.createdOn)),
        updatedOn: moment(new Date(tag.updatedOn)),
      };
    }).filter(Boolean) as LeadTag[];
  }
);

export const getCustomTags = createSelector(
  [getTags],
  (tags) => tags.filter((t) => !t.defaultType)
);

export const getTagsForLead = (state: RootState, leadId: number) => {
	const ids = state.leadTags.byLeadId[leadId];
	return getTagsByIds(state, ids);
};

// Optimized version that doesn't depend on entire state
export const getTagsForLeadOptimized = createSelector(
  [selectLeadTagsById, selectLeadTagsByLeadId, selectLeadIdParam],
  (byId, byLeadId, leadId) => {
    const ids = byLeadId[leadId] ?? EMPTY_NUM_IDS;
    return ids.map((id) => {
      const tag = byId[id];
      if (!tag) return undefined;
      
      const defaultType = LeadTagType.getById(tag.text);
      return {
        ...tag,
        defaultType,
        createdOn: moment(new Date(tag.createdOn)),
        updatedOn: moment(new Date(tag.updatedOn)),
      };
    }).filter(Boolean) as LeadTag[];
  }
);

export const getTagsForLeads = (state: RootState, leadIds: number[]) => {
	return getTags(state).filter(leadTag => leadIds.includes(leadTag.leadId));
};

export const getCustomTagsForLead = (state: RootState, leadId: number) => {
	return getTagsForLead(state, leadId).filter((tag) => !tag.defaultType);
};

export const getTypeTagsForLead = (state: RootState, leadId: number) => {
	return getTagsForLead(state, leadId).filter((tag) => tag.defaultType && tag.defaultType.type);
};

export const getBookmarkTagForLead = (state: RootState, leadId: number) => {
	return getTagsForLead(state, leadId).find((tag) => tag.defaultType && tag.defaultType.bookmarked);
};

export const getLeadTagByText = (state: RootState, leadId: number, text: string) => {
	return getTagsForLead(state, leadId).find((tag) => tag.text.toLowerCase() === text.toLowerCase());
};

export const getCustomTagTexts = createSelector(
	(state: RootState) => state,
	(state) => {
		const results = new Set<string>();
		getCustomTags(state).forEach((tag) => results.add(tag.text));
		return Array.from(results);
	}
);

export const getAllTagStrings = createSelector(
	(state: RootState) => state,
	(state) => {
		const customTags = getCustomTagTexts(state);
		let texts = [
			...customTags,
			...LeadTagType.values().map(type => type.label),
			"Lead",
			"Prospect",
			"Active Client",
			"Past Client",
			"Buyer Agent",
			"Seller Agent",
			"Mortgage Brokers",
			"Loan Officer",
			"Residential",
			"Commercial",
			"Land",
			"Pre-Approved",
			"Has Loan",
			"Made Offer",
			"Closing Scheduled",
			"Has Active Listing",
			"Attended Showing",
			"Pre-Qualified $150K",
			"Pre-Qualified $200K",
			"Pre-Qualified $250K",
			"Pre-Qualified $300K",
			"Pre-Qualified $350K",
			"Pre-Qualified $400K",
			"Pre-Qualified $450K",
			"Pre-Qualified $500K",
			"Pre-Qualified $550K",
			"Pre-Qualified $600K",
			"Pre-Qualified $650K",
			"Pre-Qualified $700K",
			"Pre-Qualified $750K",
			"Pre-Qualified $800K",
			"Pre-Qualified $850K",
			"Pre-Qualified $900K",
			"Pre-Qualified $950K",
			"Pre-Qualified $1M",
			"Pre-Qualified $1-1.5M",
			"Pre-Qualified $1.5-2M",
			"Pre-Qualified $2-2.5M",
			"Pre-Qualified $2.5-3M",
			"Pre-Qualified $3M+",
		];
		return Array.from(new Set(texts));
	}
);

// Listings

export const getListingById = (state: RootState, id?: string): Listing | undefined => {
	if (id) {
		const listing = state.listings.byId[id];
		if (listing) {
			return {
				...listing,
				get propertyType() {
					return PropertyType.getById(listing.propertyType);
				},
				get address() {
					const parts = listing.address.split("\\n");
					const result: [string, string] = [
						(parts[0] || "").trim().toUpperCase(),
						(parts[1] || "").trim().toUpperCase(),
					];
					return result;
				},
				get addressOneLine() {
					return this.address.join(" ");
				},
				get bathrooms() {
					const result: number[] = [];
					let { fullBathrooms, partialBathrooms } = listing;
					if (fullBathrooms) {
						result.push(fullBathrooms);
					}
					if (!fullBathrooms && partialBathrooms) {
						result.push(0);
					}
					if (partialBathrooms) {
						result.push(partialBathrooms);
					}
					return result.join(" | ");
				},
				get priceLabel() {
					return formatPrice(listing.price);
				},
			};
		}
	}
};

export const getListingsByIds = (state: RootState, ids?: string[]) => {
	ids = ids || [];
	return ids.map((id) => getListingById(state, id)).filter(Boolean) as Listing[];
};

export const getListings = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.listings.byId);
		return getListingsByIds(state, ids); 
	}
);


// Markets

const selectMarketsById = (s: RootState) => s.markets.byId;
const selectMarketIdParam = (_: RootState, id: number) => id;

export const getMarketById = createSelector(
  [selectMarketsById, selectState, selectMarketIdParam],
  (byId, state, id): Market | undefined => {
    const market = byId[id];
    if (!market || !market.criteria) return undefined;

    return {
      id: market.id,
      photoUrl: market.photoUrl,
      name: market.name,
      criteria: market.criteria,
      header: market.header,
      footer: market.footer,
      subhead: market.subhead,
      description: market.description,
      sortOrder: market.sortOrder,
      showOnIndex: market.showOnIndex,
      listingReportSupported: !!market.listingReportSupported,
      openHomesReportSupported: !!market.openHomesReportSupported,
      marketReportSupported: !!market.marketReportSupported,
      inactive: !!market.inactive,

      get reports() {
        return getMarketReportsByMarketId(state, market.id);
      },
      get listingReport() {
        return this.reports.find((r) => r.type.listing);
      },
      get openHomesReport() {
        return this.reports.find((r) => r.type.openHomes);
      },
      get marketReport() {
        return this.reports.find((r) => r.type.market);
      },
      get marketReportSubscriptions() {
        let subs: MarketReportSubscription[] = [];
        this.reports.forEach((r) => {
          if (r.subscriptions) subs = subs.concat(r.subscriptions);
        });
        return subs;
      },
      get marketReportTypes() {
        return [
          this.listingReport && market.listingReportSupported && this.listingReport.type,
          this.openHomesReport && market.openHomesReportSupported && this.openHomesReport.type,
          this.marketReport && market.marketReportSupported && this.marketReport.type,
        ].filter(Boolean) as MarketReportType[];
      },
    };
  }
);

export const getMarketsByIds = createSelector(
  [selectState, selectNumberIdsParam],
  (state, ids): Market[] =>
    ids.map((id) => getMarketById(state, id)).filter(Boolean) as Market[]
);

const selectMarketIds = createSelector(
  [selectMarketsById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getMarkets = createSelector(
  [selectState, selectMarketIds],
  (state, ids) => getMarketsByIds(state, ids)
);

export const getMarketsOptimized = createSelector(
	(state: RootState) => state.markets.byId,
	(state: RootState) => state.marketReports.byId,
	(state: RootState) => state.marketReportSubscriptions.byId,
	(state: RootState) => state.marketReportSubscriptions.byMarketReportId,
	(state: RootState) => state.leads.byId,
	(marketsById, marketReportsById, subscriptionsById, subscriptionsByMarketReportId, leadsById) => {
		const ids = Object.keys(marketsById).map((id) => parseInt(id));
		return ids.map((id) => {
			const market = marketsById[id];
			if (!market || !market.criteria) return undefined;
			
			// Get market reports for this market directly from state
			const marketReports = Object.values(marketReportsById).filter(report => {
				if (report.marketId === id) {
					const type = MarketReportType.getById(report.typeId);
					if (type && (
						type.listing || 
						(type.market && market.marketReportSupported) || 
						(type.openHomes && market.openHomesReportSupported)
					)) {
						return true;
					}
				}
				return false;
			}).map(report => {
				const type = MarketReportType.getById(report.typeId);
				// Get subscriptions for this market report
				const subscriptionIds = subscriptionsByMarketReportId[report.id] || [];
				const subscriptions = subscriptionIds
					.map(subId => {
						const subResource = subscriptionsById[subId];
						if (!subResource) return undefined;
						const leadResource = leadsById[subResource.leadId];
						if (!leadResource) return undefined;

						// Create a minimal MarketReportSubscription with the necessary fields
						// The component only needs lead.id and inactive, so we create a minimal lead object
						return {
							id: subResource.id,
							inactive: subResource.inactive,
							lead: {
								id: leadResource.id,
								label: leadResource.name && leadResource.name.trim() ? leadResource.name.trim() : leadResource.email,
							} as Lead,
							report: {
								id: report.id,
								type: type,
							} as MarketReport, // Include id and type for bulk operations
							emailSents: [],
							emailClicks: [],
							createdOn: subResource.createdOn ? moment(new Date(subResource.createdOn)) : undefined,
						} as MarketReportSubscription;
					})
					.filter(Boolean) as MarketReportSubscription[];

				return type ? {
					...report,
					type,
					market: {
						id: market.id,
						name: market.name,
						criteria: market.criteria,
						listingReportSupported: market.listingReportSupported || false,
						openHomesReportSupported: market.openHomesReportSupported || false,
						marketReportSupported: market.marketReportSupported || false,
						reports: [], // Minimal to avoid circular dependencies
						marketReportTypes: []
					} as Market,
					subscriptions,
				} as MarketReport : undefined;
			}).filter((report): report is MarketReport => report !== undefined);
			
			return {
				id: market.id,
				photoUrl: market.photoUrl,
				name: market.name,
				criteria: market.criteria,
				header: market.header,
				footer: market.footer,
				subhead: market.subhead,
				description: market.description,
				sortOrder: market.sortOrder,
				showOnIndex: market.showOnIndex,
				listingReportSupported: market.listingReportSupported || false, 
				openHomesReportSupported: market.openHomesReportSupported || false, 
				marketReportSupported: market.marketReportSupported || false, 
				inactive: market.inactive || false,
				get reports() {
					return marketReports;
				},
				get listingReport() {
					return marketReports.find((report) => report.type.listing);
				},
				get openHomesReport() {
					return marketReports.find((report) => report.type.openHomes);
				},
				get marketReport() {
					return marketReports.find((report) => report.type.market);
				},
				get marketReportSubscriptions() {
					let subscriptions: MarketReportSubscription[] = [];
					marketReports.forEach((report) => {
						if (report.subscriptions) {
							subscriptions = [...subscriptions, ...report.subscriptions];
						}
					});
					return subscriptions;
				},
				get marketReportTypes() {
					return [
						this.listingReport && market.listingReportSupported && this.listingReport.type,
						this.openHomesReport && market.openHomesReportSupported && this.openHomesReport.type,
						this.marketReport && market.marketReportSupported && this.marketReport.type,
					].filter(Boolean) as MarketReportType[];
				}
			} as Market;
		}).filter(Boolean) as Market[];
	}
);

export const getPromotedMarkets = createSelector(
	(state: RootState) => state.markets.byId,
	(marketsById) => {
		return Object.values(marketsById)
			.filter(market => !market.inactive && market.promotedInAreaSelector)
			.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
	}
);

// market settings

export const getmarketSettings = createSelector(
	(state: RootState) => state.marketSettings,
	(marketSettingsState) => {
		if (marketSettingsState === undefined) {
			return undefined;
		}
		if (marketSettingsState) {
			return {
				listingCarouselColumns: marketSettingsState.listingCarouselColumns,
				communitiesLayout: marketSettingsState.communitiesLayout
			} as MarketSettings;
		}
		return undefined;
	}
);

// MarketListings

export const getMarketListingById = (state: RootState, id?: number): MarketListing | undefined => {
	if (id) {
		const marketListing = state.marketListings.byId[id];
		if (marketListing) {
			return {
				...marketListing,
				listing: getListingById(state, marketListing.listing),
			}
		}
	}
};

export const getMarketListingsByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getMarketListingById(state, id)).filter(Boolean) as MarketListing[];
};

export const getMarketListings = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.marketListings.byId).map((id) => parseInt(id));
		return getMarketListingsByIds(state, ids); 
	}
);
export const getMarketListingByMarketId = (state: RootState, id: number) => {
	return getMarketListings(state).filter((value) => value.marketId === id); 
};

// MarketReport 

const selectMarketReportsById = (s: RootState) => s.marketReports.byId;
const selectMarketReportIdParam = (_: RootState, id?: number) => (id ?? null);

const selectMarketReportIds = createSelector(
  [selectMarketReportsById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getMarketReportById = createSelector(
  [selectMarketReportsById, selectState, selectMarketReportIdParam],
  (byId, state, id): MarketReport | undefined => {
    if (id == null) return undefined;

    const mr = byId[id];
    if (!mr) return undefined;

    const type = MarketReportType.getById(mr.typeId);
    if (!type) return undefined;

    return {
      ...mr,
      type,
      customUrl: mr.customUrl,
      get market() {
        return getMarketById(state, mr.marketId);
      },
      get subscriptions() {
        return getMarketReportSubscriptionsForMarketReport(state, mr.id);
      },
      inactive: mr.inactive,
    };
  }
);

export const getMarketReportsByIds = createSelector(
  [selectState, selectNumberIdsParam],
  (state, ids): MarketReport[] =>
    ids.map((id) => getMarketReportById(state, id)).filter(Boolean) as MarketReport[]
);

export const getMarketReports = createSelector(
  [selectState, selectMarketReportIds],
  (state, ids) => getMarketReportsByIds(state, ids)
);

export const getMarketReportsByMarketId = createSelector(
  [getMarketReports, selectMarketIdParam],
  (reports, marketId) =>
    marketId == null
      ? []
      : reports.filter((report) => {
          const m = report.market; // lazily resolved via getter
          if (!m || m.id !== marketId) return false;
          return (
            report.type.listing ||
            (report.type.market && m.marketReportSupported) ||
            (report.type.openHomes && m.openHomesReportSupported)
          );
        })
);


export const getMarketReportSubscriptionsForLeadSel = createSelector(
  [(_: RootState, leadId?: number) => leadId ?? null, (s: RootState) => s],
  (leadId, state) => (leadId == null ? [] : (getMarketReportSubscriptionsForLead(state, leadId) ?? []))
);

export const getUnsubscribedReportsByLeadId = createSelector(
  [getMarketReports, selectLeadIdParam, getMarketReportSubscriptionsForLeadSel],
  (reports, leadId, subs) => {
    if (leadId == null) return [];
    const subscribedIds = new Set<number>(subs.map(s => s.report.id));
    return reports.filter(r => !subscribedIds.has(r.id));
  }
);

export const getUnsubscribedMarketReportsByMarketId = createSelector(
  [
    (state: RootState, marketId?: number, _leadId?: number) =>
      marketId == null ? [] : getMarketReportsByMarketId(state, marketId),

    (state: RootState, _marketId?: number, leadId?: number) =>
      leadId == null ? [] : (getMarketReportSubscriptionsForLead(state, leadId) ?? []),
    (_: RootState, marketId?: number, _leadId?: number) => marketId ?? null,
    (_: RootState, _marketId?: number, leadId?: number) => leadId ?? null,
  ],
  (reports, subs, marketId, leadId) => {
    if (marketId == null || leadId == null) return [];
    const subscribed = new Set<number>(subs.map(s => s.report.id));
    return reports.filter(r => !subscribed.has(r.id));
  }
);

// MarketReportSubscriptions

const selectMrSubsById = (s: RootState) => s.marketReportSubscriptions.byId;
const selectMrSubsByLeadId = (s: RootState) => s.marketReportSubscriptions.byLeadId;
const selectMrSubIdParam = (_: RootState, id?: number) => id ?? null;
const selectReportIdParam = (_: RootState, reportId?: number) => reportId ?? null;

const selectMrSubIds = createSelector(
  [selectMrSubsById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getMarketReportSubscriptionById = createSelector(
  [selectMrSubsById, selectState, selectMrSubIdParam],
  (byId, state, id): MarketReportSubscription | undefined => {
    if (id == null) return undefined;

    const subscription = byId[id];
    if (!subscription) return undefined;

    const report = getMarketReportById(state, subscription.marketReportId);
    const lead = getLeadById(state, subscription.leadId);
    if (!report || !lead) return undefined;

    return {
      ...subscription,
      lead,
      report,
      createdOn: subscription.createdOn ? moment(new Date(subscription.createdOn)) : undefined,
      get emailSents() {
        return getEmailSentsForLeadAndMarketReport(state, subscription.leadId, subscription.marketReportId);
      },
      get emailClicks() {
        return getEmailClicksForLeadAndMarketReport(state, subscription.leadId, subscription.marketReportId);
      },
    };
  }
);

export const getMarketReportSubscriptionsByIds = createSelector(
  [selectState, selectIdsParam],
  (state, ids): MarketReportSubscription[] =>
    ids.map((id) => getMarketReportSubscriptionById(state, id)).filter(Boolean) as MarketReportSubscription[]
);

// Recompute only when the slice reference changes
export const getMarketReportSubscriptions = createSelector(
  [selectState, selectMrSubIds],
  (state, ids) => getMarketReportSubscriptionsByIds(state, ids)
);

export const getMarketReportSubscriptionsForLead = createSelector(
  [selectState, selectMrSubsByLeadId, selectLeadIdParam],
  (state, byLeadId, leadId) => {
    if (leadId == null) return [];
    const ids = byLeadId[leadId] ?? EMPTY_NUM_IDS;
    return getMarketReportSubscriptionsByIds(state, ids);
  }
);

export const getMarketReportSubscriptionsForMarketReport = createSelector(
  [getMarketReportSubscriptions, selectReportIdParam],
  (subs, reportId) => (reportId == null ? [] : subs.filter((s) => s.report.id === reportId))
);

export const getActiveMarketReportSubscriptionsForLead = createSelector(
  [
    (state: RootState, leadId?: number) =>
      leadId == null ? [] : getMarketReportSubscriptionsForLead(state, leadId),
    selectLeadIdParam,
  ],
  (subs) => subs.filter((s) => !s.inactive)
);

// MobileDialogDate

export const getMobileDialogDate = createSelector(
	(state: RootState) => state.mobileDialogDate.date,
	(date) => {
		if (date) {
			return moment(date);
		}
		return undefined;
	}
);

export const getMobileDialogClickLogin = (state: RootState): boolean | undefined => {
	if (state.mobileDialogDate.clickMobileLogin) {
		return state.mobileDialogDate.clickMobileLogin;
	} 
}

// Neighborhoods

export const getNeighborhoodById = createSelector(
	(state: RootState) => state.neighborhoods.byId,
	(state: RootState, id: number) => id,
	(neighborhoodsById, id) => {

			const neighborhood = neighborhoodsById[id];
			if (neighborhood) {
				return {
					...neighborhood,
				} as Neighborhood;
			}
	}
);

export const getNeighborhoodsByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getNeighborhoodById(state, id)).filter(Boolean) as Neighborhood[];
};

export const getNeighborhoods = createSelector(
	(state: RootState) => state.neighborhoods.byId,
	(neighborhoodsById) => {
		const ids = Object.keys(neighborhoodsById).map((id) => parseInt(id));
		return ids.map((id) => {
			const neighborhood = neighborhoodsById[id];
			return neighborhood ? { ...neighborhood } as Neighborhood : undefined;
		}).filter(Boolean) as Neighborhood[];
	}
);

// OfficeBoards

export const getOfficeBoardById = (
	state: RootState,
	id?: number
): OfficeBoard | undefined => {
	if (id) {
		const officeBoard = state.officeBoards.byId[id];
		if (officeBoard) {
			return {
				...officeBoard,
			};
		}
	}
};

export const getOfficeBoardsByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids
		.map((id) => getOfficeBoardById(state, id))
		.filter(Boolean) as OfficeBoard[];
};

export const getOfficeBoards = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.officeBoards.byId).map((id) => parseInt(id));
		return getOfficeBoardsByIds(state, ids);
	}
);

export const getOfficeBoardsForOffice = (state: RootState, officeId: number) => {
	const ids = state.officeBoards.byOfficeId[officeId];
	return getOfficeBoardsByIds(state, ids);
};

// Offices


export const getOfficeById = createSelector(
  [selectOfficesById, selectIdParam],
  (officesById, id): Office | undefined => {
    if (!id) return undefined;
    const office = officesById[id];
    if (!office) return undefined;

    return {
      ...office,
      name: office.name ?? '',
      address: office.address ?? '',
      city: office.city ?? '',
      priority: office.priority ?? 0,
    };
  }
);

export const getOfficesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getOfficeById(state, id)).filter(Boolean) as Office[];
};

export const getOffices = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.offices.byId).map((id) => parseInt(id));
		return getOfficesByIds(state, ids); 
	}
);

/**
 * Offices in their custom display order. Priority is the position column (the office analogue
 * of Agent.sortOrder), so this mirrors getAgentsByCustomSort.
 */
export const getOfficesByCustomSort = (state: RootState) => {
	const offices = getOffices(state);
	return [...offices].sort(function(a, b) {
		const priorityA = a.priority ?? Number.MAX_VALUE;
		const priorityB = b.priority ?? Number.MAX_VALUE;
		return priorityA - priorityB;
	});
};

// OneUsers 


export const getOneUser = createSelector(
	(state: RootState) => state.oneUsers.user,
	(user) => user
);

// Pages 

export const getPageById = (state: RootState, id?: number): Page | undefined => {
	if (id == null) return undefined;
	const page = state.pages.list.find(p => p.id === id);
	if (!page) return undefined;
	const type = PageType.getById(page.typeId);
	return type ? { ...page, type } : undefined;
};

export const getPagesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getPageById(state, id)).filter(Boolean) as Page[];
};

export const getPagesByTypes = (state: RootState, pageTypes?: PageType[]) => {
	pageTypes = pageTypes || [];
	return pageTypes.map((pageType) => getPageByTypeId(state, pageType.id)).filter(Boolean) as Page[];
};

export const getPageByTypeId = (state: RootState, id: number) => {
	return getPages(state).find((page) => page.type.id === id);
};

export const getPages = createSelector(
	(state: RootState) => state.pages.list,
	(list) =>
		list
			.map(page => {
				const type = PageType.getById(page.typeId);
				return type ? { ...page, type } : undefined;
			})
			.filter(Boolean) as Page[],
);

// Quota

export const getQuota = createSelector(
	(state: RootState) => state.quota.quota,
	(quota) => {
		if (quota) {
			return {
				...quota,
				blastCampaignMessageQueueRemaining: quota.blastCampaignMessageQueueLimit - quota.blastCampaignMessageQueueCount,
			} as Quota;
		}
		return undefined;
	}
);

// Resellers 

export const getResellerById = (state: RootState, id?: number): Reseller | null | undefined => {
	if (id) {
		const reseller = state.resellers.byId[id];
		if (reseller) {
			return {
				...reseller,
			};
		}
	}
}

// SavedListing

export const getSavedListingById = (state: RootState, id?: number): SavedListing | undefined => {
	if (id) {
		const savedListing = state.savedListings.byId[id];
		if (savedListing) {
			const lead = getLeadById(state, savedListing.leadId);
			if (lead) {
				return {
					...savedListing,
					lead,
					listing: getListingById(state, savedListing.listing),
					createdOn: moment(new Date(savedListing.createdOn)),
					modifiedOn: savedListing.modifiedOn ? moment(new Date(savedListing.modifiedOn)) : undefined,
					deleted: savedListing.deleted ? savedListing.deleted : false,
				}
			}
		}
	}
};

export const getSavedListingsByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getSavedListingById(state, id)).filter(Boolean) as SavedListing[];
};

export const getSavedListings = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.savedListings.byId).map((id) => parseInt(id));
		return getSavedListingsByIds(state, ids); 
	}
);

export const getSavedListingsForLead = (state: RootState, leadId: number) => {
	const ids = state.savedListings.byLeadId[leadId];
	return getSavedListingsByIds(state, ids);
};

export const getActiveSavedListingsForLead = (state: RootState, leadId: number) => {
	return getSavedListingsForLead(state, leadId)
		.filter(savedListing => savedListing.listing)
		.filter((savedListing) => !savedListing.deleted)
	;
};

// SavedSearches

export const getSavedSearchById = (state: RootState, id?: number): SavedSearch | undefined => {
	if (id) {
		const savedSearch = state.savedSearches.byId[id];
		if (savedSearch) {
			return {
				...savedSearch,
				get label() {
					return savedSearch.name ? savedSearch.name : `Saved Search ${numberedDateFormatter(this.createdOn)}`;
				},
				criteria: savedSearch.criteria,
				createdOn: moment(new Date(savedSearch.createdOn)),
				modifiedOn: savedSearch.modifiedOn ? moment(new Date(savedSearch.modifiedOn)) : undefined,
				get emailSents() {
					return getEmailSentsForSavedSearch(state, savedSearch.id);
				},
				get emailClicks() {
					return getEmailClicksForSavedSearch(state, savedSearch.id);
				},
				get resultCount() {
					return getSavedSearchResultTotalForSavedSearch(state, savedSearch.id);
				},
			}
		}
	}
};

export const getSavedSearchesByIds = (state: RootState, ids?: number[]) => {
	ids = ids || [];
	return ids.map((id) => getSavedSearchById(state, id)).filter(Boolean) as SavedSearch[];
};

export const getSavedSearches = createSelector(
	(state: RootState) => state,
	(state) => {
		const ids = Object.keys(state.savedSearches.byId).map((id) => parseInt(id));
		return getSavedSearchesByIds(state, ids); 
	}
);

export const getSavedSearchesForLead = (state: RootState, leadId: number) => {
	const ids = state.savedSearches.byLeadId[leadId];
	return getSavedSearchesByIds(state, ids);
};

export const getActiveSavedSearchesForLead = (state: RootState, leadId: number) => {
	return getSavedSearchesForLead(state, leadId).filter((savedSearch) => {
		return !savedSearch.inactive;
	});
};

// SavedSearchResultTotals

const getSavedSearchResultTotalById = (state: RootState, id?: number): number | undefined => {
	if (id) {
		const savedSearchResultTotal = state.savedSearchResultTotals.byId[id];
		if (savedSearchResultTotal) {
			return savedSearchResultTotal.total;
		}
	}
}

export const getSavedSearchResultTotalForSavedSearch = (state: RootState, savedSearchId: number) => {
	return getSavedSearchResultTotalById(state, savedSearchId);
}

// SidebarMenu 

export const getActive = (state: RootState) => {
	return state.sidebarMenu.active;
}

export const getOpen = (state: RootState) => {
	return state.sidebarMenu.open;
}

// SiteLinks

export const getSiteLinkById = (state: RootState, id: string): SiteLink | undefined => {
	if (id) {
		const SiteLink = state.siteLinks.byId[id];
		if (SiteLink) {
			const linkType = SiteLinkType.getById(id);
			if (linkType) {
				return {
					id: SiteLink.id,
					url: SiteLink.url,
					type: linkType,
					group: linkType.group,
				}
			}
		}
	}
}

export const getSiteLinks = createSelector(
	(state: RootState) => state.siteLinks.byId,
	(state: RootState) => state,
	(byId, state) => {
		return Object.keys(byId)
		.map((id) => getSiteLinkById(state, id))
		.filter(Boolean) as SiteLink[];
	}
);

// Tasks

const selectTasksById = (s: RootState) => s.tasks.byId;
const selectTaskIdParam = (_: RootState, id?: number) => (id ?? null);

export const getTaskById = createSelector(
  [selectTasksById, selectTaskIdParam],
  (byId, id): Task | undefined => {
    if (id == null) return undefined;
    const task = byId[id];
    if (!task) return undefined;

    const type = task.taskTypeId ? TaskType.getById(task.taskTypeId) : undefined;
    const status = TaskStatus.getById(task.taskStatusTypeId);
    if (!type || !status) return undefined;

    return {
      ...task,
      type,
      status,
      startOn:  moment(new Date(task.startOn)),
      endOn:    moment(new Date(task.endOn)),
      updatedOn:moment(new Date(task.updatedOn)),
      createdOn:moment(new Date(task.createdOn)),
    };
  }
);

export const getTasksByIds = createSelector(
  [selectState, selectNumberIdsParam],
  (state, ids): Task[] =>
    ids.map((id) => getTaskById(state, id)).filter(Boolean) as Task[]
);

const selectTaskIds = createSelector(
  [selectTasksById],
  (byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getTasks = createSelector(
  [selectState, selectTaskIds],
  (state, ids) => getTasksByIds(state, ids)
);

export const getLeadsWithTasks = createSelector(
  [getTasks, getLeadsOptimized],
  (tasks, allLeads) => {
    const ids: number[] = [];
    const seen = new Set<number>();
    for (const t of tasks) {
      const id = t.leadId;
      if (id != null && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }

    if (ids.length === 0 || allLeads.length === 0) return [];
    const byId = new Map<number, Lead>(allLeads.map(l => [l.id, l]));
    return ids.map(id => byId.get(id)).filter((x): x is Lead => !!x);
  }
)

export const getTasksForLead = (state: RootState, leadId: number) => {
	const ids = state.tasks.byLeadId[leadId];
	return getTasksByIds(state, ids);
};

export const getOpenTasks = createSelector(
	(state: RootState) => state,
	(state) => {
		return getTasks(state).filter(task => {
			return task.status.active || (task.status.complete && task.updatedOn >= moment().subtract(2, "minutes"));
		});
	}
);

export const getOpenTasksForLead = (state: RootState, leadId: number) => {
	return getOpenTasks(state).filter((task) => task.leadId === leadId);
};

export const getChronologicalActiveTasks = createSelector(
	(state: RootState) => state,
	(state) => {
		return getOpenTasks(state).sort((task1, task2) => task1.endOn.valueOf() - task2.endOn.valueOf());
	}
);

export const getActiveLeadTasksDueThisWeek = createSelector(
	(state: RootState) => state,
	(state) => {
		return getChronologicalActiveTasks(state).filter(task => task.endOn < moment().add("w", 1));
	}
);

// TasksPage 

const selectFilters = (s: RootState) => s.taskPage.filters;

export const getTasksPageFilters = (s: RootState) => s.taskPage.filters;

export const getTasksPageFiltersCount = createSelector(
  [getTasksPageFilters],
  (filters) => {
    let count = 0;
    Object.keys(filters).forEach((name) => {
      // @ts-ignore
      const value = filters[name];
							if (value instanceof Array) {
								count += value.length;
							} else if (!!value) {
								count++;
							}
    });
    return count;
  }
);

export const getFilteredTasks = createSelector(
  [getTasks, getTasksPageFilters],
  (inputTasks, filters) => {
    let tasks = inputTasks.slice();

    if (filters.agent?.length) {
      const allowed = new Set<number>(filters.agent);
      tasks = tasks.filter(t => t.agentId != null && allowed.has(t.agentId));
    }

    if (filters.type?.length) {
      const allowed = new Set<number>(filters.type);
      tasks = tasks.filter(t => t.type && allowed.has(t.type.id));
    }

    if (filters.status?.length) {
      const allowed = new Set<number>(filters.status);
      const activeFilterIsSet = allowed.has(TaskStatus.ACTIVE.id);
      const cutoff = moment().subtract(2, 'minutes');

      tasks = tasks.filter(t => {
        const isRecentComplete =
          activeFilterIsSet &&
          t.status.id === TaskStatus.COMPLETE.id &&
          t.updatedOn >= cutoff;

        return isRecentComplete || allowed.has(t.status.id);
      });
    }

    if (filters.priority?.length) {
      const allowed = new Set<boolean>(filters.priority);
      tasks = tasks.filter(t => allowed.has(t.priority));
    }

    if (filters.today) {
      const todayStr = moment().format('YYYYMMDD');
      tasks = tasks.filter(t => t.endOn.format('YYYYMMDD') <= todayStr);
    }

    if (filters.lead != null) {
      tasks = tasks.filter(t => t.leadId === filters.lead);
    }

    return tasks;
  }
);

const selectAgentIdParam = (_: RootState, agentId?: number) => agentId ?? null;

export const getFilteredTasksByAgentId = createSelector(
  [getFilteredTasks, selectAgentIdParam],
  (tasks, agentId) => (agentId == null ? [] : tasks.filter(t => t.agentId === agentId))
);


// UserAuth

export const getUserAuthError = createSelector(
	(state: RootState) => state.userAuth.error,
	(error) => error
);

export const getUserAuth = createSelector(
	(state: RootState) => state.authentication.user,
	(user): UserAuth | undefined => {
		if (user) {
			return {
				username: user.username
			}
		} else {
			return;
		}
	}
);

// Natural Language Search Suggestions

const selectNaturalLanguageSearchSuggestionsById = (s: RootState) => s.naturalLanguageSearchSuggestions.byId;

export const getNaturalLanguageSearchSuggestionById = createSelector(
	[selectNaturalLanguageSearchSuggestionsById, selectIdParam],
	(byId, id): NaturalLanguageSearchSuggestionResource | undefined => {
		if (id == null) return undefined;
		return byId[id];
	}
);

export const getNaturalLanguageSearchSuggestionsByIds = createSelector(
	[selectNaturalLanguageSearchSuggestionsById, selectNumberIdsParam],
	(byId, ids): NaturalLanguageSearchSuggestionResource[] =>
		ids.map((id) => byId[id]).filter(Boolean) as NaturalLanguageSearchSuggestionResource[]
);

const selectNaturalLanguageSearchSuggestionIds = createSelector(
	[selectNaturalLanguageSearchSuggestionsById],
	(byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getNaturalLanguageSearchSuggestions = createSelector(
	[selectNaturalLanguageSearchSuggestionsById, selectNaturalLanguageSearchSuggestionIds],
	(byId, ids) => ids.map((id) => byId[id]).filter(Boolean) as NaturalLanguageSearchSuggestionResource[]
);

export const getPromotedNaturalLanguageSearchSuggestions = createSelector(
	[getNaturalLanguageSearchSuggestions],
	(suggestions) => suggestions.filter(s => s.promoted).sort((a, b) => a.id - b.id)
);

// Area Selector Placeholders

const selectAreaSelectorPlaceholdersById = (s: RootState) => s.areaSelectorPlaceholders.byId;

export const getAreaSelectorPlaceholderById = createSelector(
	[selectAreaSelectorPlaceholdersById, selectIdParam],
	(byId, id): AreaSelectorPlaceholderResource | undefined => {
		if (id == null) return undefined;
		return byId[id];
	}
);

export const getAreaSelectorPlaceholdersByIds = createSelector(
	[selectAreaSelectorPlaceholdersById, selectNumberIdsParam],
	(byId, ids): AreaSelectorPlaceholderResource[] =>
		ids.map((id) => byId[id]).filter(Boolean) as AreaSelectorPlaceholderResource[]
);

const selectAreaSelectorPlaceholderIds = createSelector(
	[selectAreaSelectorPlaceholdersById],
	(byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getAreaSelectorPlaceholders = createSelector(
	[selectAreaSelectorPlaceholdersById, selectAreaSelectorPlaceholderIds],
	(byId, ids) => ids.map((id) => byId[id]).filter(Boolean) as AreaSelectorPlaceholderResource[]
);

// Email Routing Rules

const selectEmailRoutingRulesById = (s: RootState) => s.emailRoutingRules.byId;

const selectEmailRoutingRuleIds = createSelector(
	[selectEmailRoutingRulesById],
	(byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getEmailRoutingRules = createSelector(
	[selectEmailRoutingRulesById, selectEmailRoutingRuleIds],
	(byId, ids) => ids.map((id) => byId[id]).filter(Boolean) as EmailRoutingRuleResource[]
);

// Email Recipients (MAX-3948)

const selectEmailRecipientsById = (s: RootState) => s.emailRecipients.byId;

const selectEmailRecipientIds = createSelector(
	[selectEmailRecipientsById],
	(byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getEmailRecipients = createSelector(
	[selectEmailRecipientsById, selectEmailRecipientIds],
	(byId, ids) => ids.map((id) => byId[id]).filter(Boolean) as EmailRecipientResource[]
);

// Off-Market Listings

const selectOffMarketListingsById = (s: RootState) => s.offMarketListings.byId;

const selectOffMarketListingIds = createSelector(
	[selectOffMarketListingsById],
	(byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getOffMarketListings = createSelector(
	[selectOffMarketListingsById, selectOffMarketListingIds],
	(byId, ids) => ids.map((id) => byId[id]).filter(Boolean) as OffMarketListingResource[]
);

const selectOffMarketListingIdParam = (_: RootState, id?: number) => (id ?? null);

export const getOffMarketListingById = createSelector(
	[selectOffMarketListingsById, selectOffMarketListingIdParam],
	(byId, id): OffMarketListingResource | undefined => (id == null ? undefined : byId[id])
);

const selectOffMarketAgentControl = (s: RootState) => s.offMarketListings.agentControl;
const selectOffMarketAutomatedFeaturedSolds = (s: RootState) => s.offMarketListings.automatedFeaturedSolds;
const selectOffMarketFeatureAgentOffice = (s: RootState) => s.offMarketListings.featureAgentOffice;
const selectOffMarketSoldsOnFeaturedYn = (s: RootState) => s.offMarketListings.soldsOnFeaturedYn;

// MAX-4194: `officeAccount` is deliberately absent here. It lives on the *resource* (the slice's
// legacy fallback reads action.payload.officeAccount) but nothing reads it off state any more —
// agentControl replaced it. selectOffMarketOfficeAccount was deleted with it; noUnusedLocals is
// off, so a leftover would not have been flagged.
export const getOffMarketListingFlags = createSelector(
	[
		selectOffMarketAgentControl,
		selectOffMarketAutomatedFeaturedSolds,
		selectOffMarketFeatureAgentOffice,
		selectOffMarketSoldsOnFeaturedYn,
	],
	(agentControl, automatedFeaturedSolds, featureAgentOffice, soldsOnFeaturedYn) => ({
		agentControl,
		automatedFeaturedSolds,
		featureAgentOffice,
		soldsOnFeaturedYn,
	})
);

export const getOffMarketListingsLoading = (state: RootState) => !!state.offMarketListings.loading;
export const getOffMarketListingsLoaded = (state: RootState) => state.offMarketListings.loaded;

// Supplemental Listings (client properties, boardId=0)

const selectClientPropertyListById = (s: RootState) => s.clientProperties.listById;
const selectClientPropertiesById = (s: RootState) => s.clientProperties.byId;

const selectClientPropertyListIds = createSelector(
	[selectClientPropertyListById],
	(listById) => Object.keys(listById).map((k) => parseInt(k, 10))
);

export const getClientPropertyListItems = createSelector(
	[selectClientPropertyListById, selectClientPropertyListIds],
	(listById, ids) => ids.map((id) => listById[id]).filter(Boolean) as ClientPropertyListItemResource[]
);

const selectClientPropertyIdParam = (_: RootState, id?: number) => (id ?? null);

export const getClientPropertyById = createSelector(
	[selectClientPropertiesById, selectClientPropertyIdParam],
	(byId, id): ClientPropertyResource | undefined => (id == null ? undefined : byId[id])
);

const selectClientPropertyAgentControl = (s: RootState) => s.clientProperties.agentControl;
const selectClientPropertyShowAgentPageColumn = (s: RootState) => s.clientProperties.showAgentPageColumn;
const selectClientPropertySupplementalOnFeaturedYn = (s: RootState) => s.clientProperties.supplementalOnFeaturedYn;

// MAX-4194: `officeAccount` is deliberately absent — see the note on getOffMarketListingFlags.
export const getClientPropertyFlags = createSelector(
	[
		selectClientPropertyAgentControl,
		selectClientPropertyShowAgentPageColumn,
		selectClientPropertySupplementalOnFeaturedYn,
	],
	(agentControl, showAgentPageColumn, supplementalOnFeaturedYn) => ({
		agentControl,
		showAgentPageColumn,
		supplementalOnFeaturedYn,
	})
);

export const getClientPropertiesLoading = (state: RootState) => !!state.clientProperties.loading;
export const getClientPropertiesLoaded = (state: RootState) => state.clientProperties.loaded;

// Listing Photo Manager (MAX-3898) — single open listing's PropertyPhoto set, shared by every
// listing editor via <PhotoManager>.

const selectListingPhotosById = (s: RootState) => s.listingPhotos.byId;

export const getListingPhotoById = createSelector(
	[selectListingPhotosById, selectIdParam],
	(byId, id): PhotoResource | undefined => (id == null ? undefined : byId[id])
);

export const getListingPhotosByIds = createSelector(
	[selectListingPhotosById, selectNumberIdsParam],
	(byId, ids): PhotoResource[] => ids.map((id) => byId[id]).filter(Boolean) as PhotoResource[]
);

const selectListingPhotoIds = createSelector(
	[selectListingPhotosById],
	(byId) => Object.keys(byId).map((k) => parseInt(k, 10))
);

export const getListingPhotos = createSelector(
	[selectListingPhotosById, selectListingPhotoIds],
	(byId, ids) => ids.map((id) => byId[id]).filter(Boolean) as PhotoResource[]
);

// Gallery order — CF's Move Up/Down operate on `priority` ascending (MAX-3898).
export const getListingPhotosOrdered = createSelector(
	[getListingPhotos],
	(photos) => [...photos].sort((a, b) => a.priority - b.priority)
);

const selectListingPhotosMainPhotoUrl = (s: RootState) => s.listingPhotos.mainPhotoUrl;
const selectListingPhotosPhotoYn = (s: RootState) => s.listingPhotos.photoYn;
const selectListingPhotosCanRevertToMls = (s: RootState) => s.listingPhotos.canRevertToMls;

export const getListingPhotosFlags = createSelector(
	[selectListingPhotosMainPhotoUrl, selectListingPhotosPhotoYn, selectListingPhotosCanRevertToMls],
	(mainPhotoUrl, photoYn, canRevertToMls) => ({ mainPhotoUrl, photoYn, canRevertToMls })
);

export const getListingPhotosLoading = (state: RootState) => !!state.listingPhotos.loading;
export const getListingPhotosLoaded = (state: RootState) => state.listingPhotos.loaded;
export const getListingPhotosError = (state: RootState) => state.listingPhotos.error;

// Leads & Contacts report (StatsContact.cfm)

export const getReportsLeadsActivities = (state: RootState): LeadContactActivityResource[] =>
	state.reportsLeads.activities;
export const getReportsLeadsTotalCount = (state: RootState): number => state.reportsLeads.totalCount;
export const getReportsLeadsTypes = (state: RootState): ContactEventTypeResource[] =>
	state.reportsLeads.types;
export const getReportsLeadsFilter = (state: RootState) => state.reportsLeads.filter;
export const getReportsLeadsLoading = (state: RootState) => !!state.reportsLeads.loading;
export const getReportsLeadsLoaded = (state: RootState) => state.reportsLeads.loaded;

// Saved Search Activity report (statsSavedSearches.cfm)

export const getSavedSearchActivityRows = (state: RootState): SavedSearchActivityRowResource[] =>
	state.savedSearchActivity.rows;
export const getSavedSearchActivityTotalCount = (state: RootState): number =>
	state.savedSearchActivity.totalCount;
export const getSavedSearchActivityLoading = (state: RootState) => !!state.savedSearchActivity.loading;
export const getSavedSearchActivityLoaded = (state: RootState) => state.savedSearchActivity.loaded;

// Saved Properties Activity report (statsSavedProperties.cfm)

export const getSavedListingsReportRows = (state: RootState): SavedListingReportResource[] =>
	state.savedListingsReport.rows;
export const getSavedListingsReportLoading = (state: RootState) => !!state.savedListingsReport.loading;
export const getSavedListingsReportLoaded = (state: RootState) => state.savedListingsReport.loaded;

// Mobile Statistics report (mobileActivity.cfm)

export const getMobileActivityRows = (state: RootState): MobileActivityReportResource[] =>
	state.mobileActivity.rows;
export const getMobileActivityTotal = (state: RootState): number => state.mobileActivity.total;
export const getMobileActivityLoading = (state: RootState) => !!state.mobileActivity.loading;
export const getMobileActivityLoaded = (state: RootState) => state.mobileActivity.loaded;

// Search Agent

/**
 * Returns undefined while the lead's profile has not been fetched. Once fetched it is a
 * profile — possibly an empty one, which the card renders as its empty state — or null
 * when the lead is not accessible.
 */
export const getSearchAgentProfileForLead = (
	state: RootState,
	leadId: number
): SearchAgentProfile | null | undefined => {
	const profile = state.searchAgent.profileByLeadId[leadId];
	if (profile === undefined) {
		return undefined;
	}
	if (profile === null) {
		return null;
	}
	const { timeline } = profile;
	return {
		...profile,
		timeline: timeline
			? {
				...timeline,
				start: timeline.start ? moment(timeline.start) : undefined,
				end: timeline.end ? moment(timeline.end) : undefined,
			}
			: undefined,
		generatedOn: profile.generatedOn ? moment(profile.generatedOn) : undefined,
	};
};

/** Newest day first, matching the order the API returns. */
export const getSearchAgentConversationDaysForLead = (
	state: RootState,
	leadId: number
): SearchAgentConversationDay[] => {
	const days = state.searchAgent.conversationDaysByLeadId[leadId] || [];
	return days.map((day) => ({
		...day,
		date: moment(day.date),
		firstMessageOn: day.firstMessageOn ? moment(day.firstMessageOn) : undefined,
		lastMessageOn: day.lastMessageOn ? moment(day.lastMessageOn) : undefined,
		aiSessionIds: day.aiSessionIds || [],
	}));
};

/**
 * One day's transcript, oldest message first — the order it was said in, and the order the
 * API returns.
 *
 * `undefined` means not fetched yet, which is what the transcript renders its loading state
 * from; an empty array means the day genuinely has nothing to show. Any role other than
 * `human` or `ai` is dropped: the API already filters to those two, so anything else would
 * be a contract change rather than something to render blind.
 */
export const getSearchAgentConversationMessages = (
	state: RootState,
	leadId: number,
	date: string
): SearchAgentConversationMessage[] | undefined => {
	const messages = (state.searchAgent.messagesByLeadIdAndDate[leadId] || {})[date];
	if (messages === undefined) {
		return undefined;
	}
	return messages
		.filter((message) => message.role === "human" || message.role === "ai")
		.map((message) => ({
			...message,
			role: message.role as SearchAgentMessageRole,
			createdOn: moment(message.createdOn),
		}));
};

/** Nulls the API uses for an unset current value become absent, the way a Lead carries them. */
const toLeadProfileSuggestionValue = (
	value: LeadProfileSuggestionValueResource | undefined
): LeadProfileSuggestionValue => {
	const { timelineStart, timelineEnd, justBrowsing, price, preApproved } = value || {};
	return {
		timelineStart: timelineStart ?? undefined,
		timelineEnd: timelineEnd ?? undefined,
		justBrowsing: justBrowsing ?? undefined,
		price: price ?? undefined,
		preApproved: preApproved ?? undefined,
	};
};

const EMPTY_LEAD_PROFILE_SUGGESTIONS: LeadProfileSuggestion[] = [];

const selectProfileSuggestionsByLeadId = (state: RootState) => state.searchAgent.profileSuggestionsByLeadId;

/**
 * Every pending suggestion for the lead, newest statement first as the server returns them
 * (field keys keep insertion order). Nothing is filtered or compared against
 * the lead: the server returns only suggestions that differ from the lead's current value.
 * A field this client does not know is the one exception, since it has no way to render it.
 */
export const getLeadProfileSuggestionsForLead = createSelector(
	[selectProfileSuggestionsByLeadId, selectLeadIdParam],
	(byLeadId, leadId): LeadProfileSuggestion[] => {
		const byId = byLeadId[leadId];
		if (!byId) {
			return EMPTY_LEAD_PROFILE_SUGGESTIONS;
		}
		const suggestions: LeadProfileSuggestion[] = [];
		Object.values(byId).forEach((suggestion) => {
			const field = LeadProfileSuggestionField.getById(suggestion.field);
			if (!field) {
				return;
			}
			suggestions.push({
				leadId,
				field,
				suggested: toLeadProfileSuggestionValue(suggestion.suggested),
				current: toLeadProfileSuggestionValue(suggestion.current),
				suggestedOn: moment(suggestion.suggestedOn),
				suggestedOnReference: suggestion.suggestedOn,
				conversationDate: suggestion.conversationDate,
			});
		});
		return suggestions;
	}
);

export const getLeadProfileSuggestionCountForLead = (state: RootState, leadId: number) => {
	return getLeadProfileSuggestionsForLead(state, leadId).length;
};
